import React, { useMemo, useRef, useState, useEffect, useContext } from "react";
import { UrlContext } from "../../contexts/UrlContext";
import { AnimatePresence, motion } from "framer-motion";
import Swal from "sweetalert2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Quill's styles
import Quill from "quill";
import { createPortal } from "react-dom";
import {
  CalendarDays,
  CheckCircle2,
  CheckCircleIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Link,
  Plus,
  Trash2,
  Users,
  Video,
  Eye,
  RefreshCw,
  Download,
  Play,
  FileSearch,
  MailPlus,
  CalendarCheck,
  X,
} from "lucide-react";

const SLOT_HEIGHT = 56;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const END_OF_DAY = 23 + 59 / 60;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MOBILE_WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];
const DATABASE_TIMEZONE = "Europe/Berlin";
const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const baseDate = new Date();

const getToken = () => {
  const tokenObj = localStorage.getItem("eph-token");
  return JSON.parse(tokenObj)?.value || "";
};

function toDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function addDays(date, amount) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + amount);
  return copy;
}

function getMonday(date) {
  const copy = new Date(date);
  const day = copy.getDay() || 7;
  copy.setDate(copy.getDate() - day + 1);
  return copy;
}

function timeLabel(hour) {
  if (Number(hour) >= END_OF_DAY) return "23:59";

  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);

  if (m >= 60) {
    const nextHour = h + 1;
    if (nextHour >= 24) return "23:59";
    return `${String(nextHour).padStart(2, "0")}:00`;
  }

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function normalizeEndHour(start, end) {
  if (Number(end) === 0) return 24;
  return end;
}

function getTop(start) {
  return (start - HOURS[0]) * SLOT_HEIGHT;
}

function getHeight(start, end) {
  const normalizedEnd = normalizeEndHour(start, end);
  return Math.max((normalizedEnd - start) * SLOT_HEIGHT, SLOT_HEIGHT / 2);
}

function formatLongDate(dateKey) {
  const date = new Date(`${dateKey}T12:00:00`);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getMonthDays(date) {
  const year = date.getFullYear();
  const month = date.getMonth();

  const first = new Date(year, month, 1);
  const start = getMonday(first);

  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function isTodayOrFuture(date) {
  return startOfDay(date) >= startOfDay(new Date());
}

function getCurrentHour() {
  return new Date().getHours();
}

function getAgendaRange(date) {
  const start = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 4, 0);

  const days = [];
  let current = new Date(start);

  while (current <= end) {
    days.push(new Date(current));
    current = addDays(current, 1);
  }

  return days;
}

function InputField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#365561]/30 focus:bg-white focus:shadow-[0_10px_24px_rgba(54,85,97,0.08)]"
      />
    </label>
  );
}

function EventModeToggle({ value, onChange }) {
  const options = [
    {
      value: "teams",
      label: "Schedule Call",
      icon: Video,
      activeBg: "bg-[#365561]",
      activeShadow: "shadow-[0_10px_24px_rgba(54,85,97,0.22)]",
      inactiveText: "text-[#365561]",
      hoverBg: "hover:bg-[#365561]/10",
    },
    {
      value: "availability",
      label: "Availability",
      icon: CheckCircle2,
      activeBg: "bg-emerald-600",
      activeShadow: "shadow-[0_10px_24px_rgba(16,185,129,0.22)]",
      inactiveText: "text-emerald-700",
      hoverBg: "hover:bg-emerald-50",
    },
  ];

  return (
    <div className="relative inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = value === option.value;

        return (
          <button
            disabled={option.value==="teams"}
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={[
              "relative z-10 inline-flex items-center gap-2 rounded-xl px-2 py-1 text-xs font-medium transition-colors duration-200 focus:outline-none",
              isActive
                ? "text-white"
                : `${option.inactiveText} ${option.hoverBg}`,
              option.value==='teams'
                ? "cursor-not-allowed text-gray-400"
                : ""
            ].join(" ")}
          >
            {isActive && (
              <motion.span
                layoutId="event-mode-toggle-pill"
                className={[
                  "absolute inset-0 -z-10 rounded-xl",
                  option.activeBg,
                  option.activeShadow,
                ].join(" ")}
                transition={{
                  type: "spring",
                  stiffness: 420,
                  damping: 32,
                }}
              />
            )}

            <Icon className="h-4 w-4" />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CalendarModeToggle({ value, onChange }) {
  return (
    <div className="relative inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      {["week", "month"].map((mode) => {
        const isActive = value === mode;

        return (
          <button
            key={mode}
            type="button"
            onClick={() => onChange(mode)}
            className={[
              "relative z-10 rounded-[8px] px-2 py-0.5 text-[9px] capitalize transition-colors duration-200 focus:outline-none",
              isActive
                ? "text-white"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-800",
            ].join(" ")}
          >
            {isActive && (
              <motion.span
                layoutId="calendar-mode-toggle-pill"
                className="absolute inset-0 -z-10 rounded-[8px] bg-[#365561] shadow-[0_10px_24px_rgba(54,85,97,0.22)]"
                transition={{
                  type: "spring",
                  stiffness: 420,
                  damping: 32,
                }}
              />
            )}

            {mode}
          </button>
        );
      })}
    </div>
  );
}

function EventChip({
  event,
  onDelete,
  onClick,
  compact = false,
  onResizeStart,
  onHoverChange,
  dayIndex = 0,
  getTooltipBounds,
  embeddedWizardMode = false,
  disableTooltip = false,
}) {
  const isTeams = event.type === "teams";
  const isClient = event.consultant == "Client";
  const canResize = !compact && Boolean(onResizeStart);
  const chipPaddingClass = embeddedWizardMode ? "px-2 py-1.5" : "px-3 py-2";
  const titleTextClass = embeddedWizardMode ? "text-[11px]" : "text-xs";
  const metaTextClass = embeddedWizardMode ? "text-[10px]" : "text-[11px]";

  function getConsultantDisplayName(event) {
    return (
      event.consultantDisplayName ||
      event.consultant ||
      event.realExpertName ||
      "Unknown Expert"
    );
  }

  const consultantName = getConsultantDisplayName(event);
  const [tooltipPosition, setTooltipPosition] = useState(null);

  const openTooltip = (mouseEvent) => {
    if (compact || disableTooltip) return;

    const tooltipWidth = 288;
    const tooltipHeight = embeddedWizardMode ? 136 : 150;
    const gap = 10;
    const cardRect = mouseEvent.currentTarget.getBoundingClientRect();
    const bounds = getTooltipBounds?.();

    const safeBounds = bounds || {
      left: 0,
      right: window.innerWidth,
      top: 0,
      bottom: window.innerHeight,
    };

    const side = dayIndex >= 4 ? "left" : "right";
    const rawLeft = side === "left"
      ? cardRect.left - tooltipWidth - gap
      : cardRect.right + gap;

    const left = Math.min(
      Math.max(rawLeft, safeBounds.left + 8),
      safeBounds.right - tooltipWidth - 8
    );

    const stickyTop = safeBounds.top + (embeddedWizardMode ? 66 : 86);
    const top = Math.min(
      Math.max(cardRect.top, stickyTop),
      safeBounds.bottom - tooltipHeight - 8
    );

    setTooltipPosition({ left, top, side });
  };

  const closeTooltip = () => {
    setTooltipPosition(null);
  };

  const displayEnd = normalizeEndHour(event.start, event.end);

  return (
    <motion.div
      layout
      onMouseEnter={(event) => {
        onHoverChange?.(event.currentTarget.dataset.eventId);
        openTooltip(event);
      }}
      onMouseLeave={() => {
        onHoverChange?.(null);
        closeTooltip();
      }}
      data-event-id={event.id}
      onClick={(e) => {
        e.stopPropagation();
        onClick(event);
      }}
      className={[
        "group relative h-full cursor-pointer overflow-hidden rounded-2xl border shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-900/15",
        chipPaddingClass,
        isTeams
          ? "border-[#365561]/20 bg-[#365561]/90 text-white"
          : isClient
            ? "border-blue-200 bg-blue-50 text-blue-800"
            : "border-emerald-200 bg-emerald-50 text-emerald-800",
      ].join(" ")}
    >
      {canResize && (
        <>
          <button
            type="button"
            onPointerDown={(e) => onResizeStart(e, event, "start")}
            onClick={(e) => e.stopPropagation()}
            className="absolute left-3 right-3 top-0 z-20 h-2 cursor-ns-resize rounded-full bg-white/0 transition hover:bg-gray-400/50 focus:outline-none"
            aria-label="Resize event start time"
          />
          <button
            type="button"
            onPointerDown={(e) => onResizeStart(e, event, "end")}
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-0 left-3 right-3 z-20 h-2 cursor-ns-resize rounded-full bg-white/0 transition hover:bg-gray-400/50 focus:outline-none"
            aria-label="Resize event end time"
          />
        </>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(event.id);
        }}
        className={[
          "absolute right-2 top-2 z-30 rounded-full p-1 opacity-0 transition group-hover:opacity-100",
          isTeams ? "bg-white/15 hover:bg-white/25" : "bg-white hover:bg-emerald-100",
        ].join(" ")}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>

      <div className="min-w-0 overflow-hidden">
        <div className="flex items-center gap-1.5 pr-6">
          {isTeams ? <Video className="h-3.5 w-3.5 shrink-0" /> : <Clock className="h-3.5 w-3.5 shrink-0" />}
          <p className={["truncate font-semibold", titleTextClass].join(" ")}>{event.title}</p>
        </div>

        {!compact && (
          <>
            <p className={["mt-1 truncate opacity-90", metaTextClass].join(" ")}>{consultantName}</p>
            <p className="truncate text-[10px] opacity-75">
              {timeLabel(event.start)} - {timeLabel(displayEnd)}
            </p>
          </>
        )}
      </div>

      {!compact &&
        tooltipPosition &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.16, ease: "easeOut" }}
              className={[
                "pointer-events-none fixed z-[9999] w-72 rounded-2xl border p-3 text-left shadow-2xl",
                isTeams
                  ? "border-[#365561]/20 bg-white text-slate-800"
                  : isClient
                    ? "border-blue-200 bg-white text-slate-800"
                    : "border-emerald-200 bg-white text-slate-800",
              ].join(" ")}
              style={{
                left: tooltipPosition.left,
                top: tooltipPosition.top,
              }}
            >
              <div
                className={[
                  "absolute top-4 h-3 w-3 rotate-45 border-slate-200 bg-white",
                  tooltipPosition.side === "left"
                    ? "right-0 translate-x-1/2 border-r border-t"
                    : "left-0 -translate-x-1/2 border-b border-l",
                ].join(" ")}
              />

              <div className="flex items-start gap-3">
                <div
                  className={[
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl",
                    isTeams
                      ? "bg-[#365561]/10 text-[#365561]"
                      : isClient
                        ? "bg-blue-50 text-blue-700"
                        : "bg-emerald-50 text-emerald-700",
                  ].join(" ")}
                >
                  {isTeams ? <Video className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {event.title}
                  </p>
                  <p className="mt-1 truncate text-xs text-slate-500">
                    {consultantName}
                  </p>
                  <p className="mt-2 text-xs font-semibold text-[#365561]">
                    {formatLongDate(event.date)} · {timeLabel(event.start)} - {timeLabel(displayEnd)}
                  </p>
                  {canResize && (
                    <p className="mt-2 text-[11px] leading-4 text-slate-400">
                      Drag the top or bottom edge to adjust the time range.
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

    </motion.div>
  );
}
function TimezoneSelector({
  selectedTimezone,
  setSelectedTimezone,
  localTimezone,
  callingFrom,
}) {
  const [open, setOpen] = useState(false);

  const timezones = useMemo(() => {
    const list =
      typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("timeZone")
        : ["Europe/Berlin"];

    return list.sort();
  }, []);

  const isDifferent = selectedTimezone !== localTimezone;

  return (
    <div className="relative mt-2 flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-[7.5px] text-[11px] font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 focus:outline-none"
      >
        <Clock className="h-3.5 w-3.5 text-[#365561]" />
        {selectedTimezone}
      </button>

      {open && (
        <div className="absolute right-0 top-9 z-50 w-[260px] rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          <select
            value={selectedTimezone}
            onChange={(e) => {
              setSelectedTimezone(e.target.value);
              setOpen(false);
            }}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 outline-none focus:border-[#365561]/30"
          >
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz === localTimezone ? "🌍 " : ""}
                {tz.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </div>
      )}

      {isDifferent && callingFrom !== "consultant" && (
        <p className="absolute w-[300px] top-full left-12 text-right text-[11px] leading-4 text-amber-600">
          Calls will be created in the selected timezone, but after refresh they
          will appear in your local timezone.
        </p>
      )}
    </div>
  );
}

function eventToDisplayEvent(event, selectedTimezone) {
  if (selectedTimezone === DATABASE_TIMEZONE) return event;

  const [year, month, day] = event.date.split("-").map(Number);

  const startHour = Math.floor(event.start);
  const startMinute = Math.round((event.start - startHour) * 60);

  const endHour = Math.floor(event.end);
  const endMinute = Math.round((event.end - endHour) * 60);

  // const startDate = new Date(year, month - 1, day, startHour, startMinute);
  // const endDate = new Date(year, month - 1, day, endHour, endMinute);

  // fix for timezone jump caused by local timezone from above line!
  const startDate = zonedWallTimeToUtc({
    date: event.date,
    hourValue: event.start,
    timeZone: DATABASE_TIMEZONE,
  });

  const endDate = zonedWallTimeToUtc({
    date: event.date,
    hourValue: event.end,
    timeZone: DATABASE_TIMEZONE,
  });

  const displayStart = new Intl.DateTimeFormat("en-CA", {
    timeZone: selectedTimezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(startDate);

  const displayEnd = new Intl.DateTimeFormat("en-CA", {
    timeZone: selectedTimezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(endDate);

  const get = (parts, type) => parts.find((p) => p.type === type)?.value;

  const startDisplayHour =
    Number(get(displayStart, "hour")) + Number(get(displayStart, "minute")) / 60;

  const endDisplayHour =
    Number(get(displayEnd, "hour")) + Number(get(displayEnd, "minute")) / 60;

  return {
    ...event,
    date: `${get(displayStart, "year")}-${get(displayStart, "month")}-${get(displayStart, "day")}`,
    start: startDisplayHour,
    end: endDisplayHour,
  };
}

function getTimezoneParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value;

  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
  };
}

function zonedWallTimeToUtc({ date, hourValue, timeZone }) {
  const [year, month, day] = date.split("-").map(Number);

  const hour = Math.floor(hourValue);
  const minute = Math.round((hourValue - hour) * 60);

  let utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute));

  for (let i = 0; i < 3; i++) {
    const parts = getTimezoneParts(utcGuess, timeZone);

    const diffMinutes =
      (Date.UTC(year, month - 1, day, hour, minute) -
        Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute
        )) /
      60000;

    utcGuess = new Date(utcGuess.getTime() + diffMinutes * 60000);
  }

  return utcGuess;
}

function convertSlotToDatabaseTimezone(slot, selectedTimezone) {
  const startUtc = zonedWallTimeToUtc({
    date: slot.date,
    hourValue: slot.start,
    timeZone: selectedTimezone,
  });

  const endUtc = zonedWallTimeToUtc({
    date: slot.date,
    hourValue: slot.end,
    timeZone: selectedTimezone,
  });

  const dbStart = getTimezoneParts(startUtc, DATABASE_TIMEZONE);
  const dbEnd = getTimezoneParts(endUtc, DATABASE_TIMEZONE);

  return {
    date: `${dbStart.year}-${String(dbStart.month).padStart(2, "0")}-${String(
      dbStart.day
    ).padStart(2, "0")}`,
    start: dbStart.hour + dbStart.minute / 60,
    end: dbEnd.hour + dbEnd.minute / 60,
  };
}

function slotToDateTime(date, hourValue) {
  const hour = Math.floor(hourValue);
  const minute = Math.round((hourValue - hour) * 60);

  return `${date}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`;
}

function SidePanel({
  projectID,
  events,
  emailData = {},
  selectedEvent,
  onEventClick,
  onDelete,
}) {

  function eventDateTime(event, point = "start") {
    const hourValue = point === "end" ? event.end : event.start;
    const hour = Math.floor(hourValue);
    const minute = Math.round((hourValue - hour) * 60);

    return new Date(
      `${event.date}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`
    );
  }

  function isCurrentOrFutureEvent(event) {
    return eventDateTime(event, "end").getTime() >= new Date().getTime();
  }

  function sortEventsAscending(a, b) {
    return eventDateTime(a, "start").getTime() - eventDateTime(b, "start").getTime();
  }

  function sortEventsDescending(a, b) {
    return eventDateTime(b, "start").getTime() - eventDateTime(a, "start").getTime();
  }

  function getConsultantDisplayName(event) {
    return (
      event.consultantDisplayName ||
      event.consultant ||
      event.realExpertName ||
      "Unknown Expert"
    );
  }

  function formatEventSlotForEmail(event) {
    return `${formatLongDate(event.date)} · ${timeLabel(event.start)} - ${timeLabel(event.end)}`;
  }

  function groupEventsByConsultant(slots) {
    return slots.reduce((acc, event) => {
      const consultantName = getConsultantDisplayName(event);

      if (!acc[consultantName]) {
        acc[consultantName] = [];
      }

      acc[consultantName].push(event);

      return acc;
    }, {});
  }

  function buildGroupedSlotsHTML(slots) {
		const groupedSlots = groupEventsByConsultant(slots);

		return Object.entries(groupedSlots)
			.sort(([nameA], [nameB]) =>
				nameA.localeCompare(nameB, undefined, {
					numeric: true,
					sensitivity: "base",
				})
			)
			.map(([consultantName, consultantSlots]) => {
				const slotsHTML = consultantSlots
					.slice()
					.sort(sortEventsAscending)
					.map((slot) => `<li>${formatEventSlotForEmail(slot)}</li>`)
					.join("");

				return `
					<div style="margin-top: 12px;">
						<div><b>${consultantName}</b></div>
						<ul>${slotsHTML}</ul>
					</div>
				`;
			})
			.join("");
	}

  return (
    <aside className="hidden md:flex max-h-[717px] flex-col rounded-3xl border border-slate-200/80 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
      <div className="mb-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-base font-semibold text-slate-900">
            Schedule panel
          </p>
        </div>

        <p className="mt-1 text-sm text-slate-500">
          Teams calls and availability slots
        </p>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto pr-1 scroll-smooth">
        {events.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
            <p className="text-sm font-medium text-slate-600">No slots found</p>
          </div>
        ) : (
          events
            .slice()
            .sort((a, b) =>
              `${b.date}-${b.start}`.localeCompare(`${a.date}-${a.start}`)
            )
            .map((event) => (
              <div
                key={event.id}
                className={[
                  "rounded-2xl border p-3 transition",
                  event.type === "teams"
                    ? selectedEvent?.id === event.id
                      ? "border-[#365561]/30 bg-[#365561]/15"
                      : "border-[#365561]/15 bg-[#365561]/85 hover:bg-[#365561]"
                    : getConsultantDisplayName(event) === "Client"
                      ? selectedEvent?.id === event.id
                        ? "border-blue-300 bg-blue-100"
                        : "border-blue-200 bg-blue-50 hover:bg-blue-100"
                      : selectedEvent?.id === event.id
                        ? "border-emerald-300 bg-emerald-100"
                        : "border-emerald-200 bg-emerald-50 hover:bg-emerald-100",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => onEventClick(event)}
                    className="min-w-0 flex-1 text-left focus:outline-none"
                  >
                    <p
                      className={[
                        "truncate text-sm font-semibold",
                        event.type === "teams" ? "text-white" : "text-slate-900",
                      ].join(" ")}
                    >
                      {event.title}
                    </p>

                    <p
                      className={[
                        "mt-1 truncate text-xs",
                        event.type === "teams" ? "text-slate-300" : "text-slate-500",
                      ].join(" ")}
                    >
											{getConsultantDisplayName(event)}
                    </p>

                    <p
                      className={[
                        "mt-1 text-xs font-medium",
                        event.type === "teams"
                          ? "text-white"
                          : getConsultantDisplayName(event) === "Client"
                            ? "text-blue-700"
                            : "text-emerald-700",
                      ].join(" ")}
                    >
                      {formatLongDate(event.date)} · {timeLabel(event.start)} -{" "}
                      {timeLabel(event.end)}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => onDelete(event.id)}
                    className="rounded-xl p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
        )}
      </div>
    </aside>
  );
}

function WeekCalendar({
  currentDate,
  events,
  selectedSlot,
  setSelectedSlot,
  onFinishSelection,
  onFinishResize,
  onEventClick,
  onDelete,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [resizingEvent, setResizingEvent] = useState(null);
  const [hoveredEventID, setHoveredEventID] = useState(null);

  const weekStart = getMonday(currentDate);
  const weekDays = DAYS.map((_, i) => addDays(weekStart, i));
  const [calendarMessage, setCalendarMessage] = useState("");
  const activeDayKey = toDateKey(currentDate);

  const scrollRef = useRef(null);
  const gridRef = useRef(null);
  const currentHourRef = useRef(null);

  const groupedEvents = useMemo(() => {
    const map = {};
    weekDays.forEach((day, index) => {
      const key = toDateKey(day);
      map[index] = events.filter((event) => event.date === key);
    });
    return map;
  }, [events, weekDays]);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getPointerSlot = (event) => {
    if (!scrollRef.current || !gridRef.current) return null;

    const gridRect = gridRef.current.getBoundingClientRect();
    const headerHeight = 80;
    const timeColumnWidth = 72;
    const gridWidth = gridRef.current.offsetWidth;
    const dayWidth = (gridWidth - timeColumnWidth) / DAYS.length;

    const x = event.clientX - gridRect.left;
    const y = event.clientY - gridRect.top - headerHeight;

    const dayIndex = clamp(
      Math.floor((x - timeColumnWidth) / dayWidth),
      0,
      DAYS.length - 1
    );

    const halfHourIndex = clamp(
      Math.floor(y / (SLOT_HEIGHT / 2)),
      0,
      HOURS.length * 2 - 1
    );

    return {
      dayIndex,
      hour: halfHourIndex * 0.5,
    };
  };

  const maybeAutoScroll = (event) => {
    if (!scrollRef.current) return;

    const rect = scrollRef.current.getBoundingClientRect();
    const edgeSize = 72;
    const step = 18;

    if (event.clientY > rect.bottom - edgeSize) {
      scrollRef.current.scrollTop += step;
      return;
    }

    if (event.clientY < rect.top + edgeSize) {
      scrollRef.current.scrollTop -= step;
    }
  };

  const buildSelectionFromRange = (startPoint, endPoint) => {
    const startAbs = startPoint.dayIndex * 24 + startPoint.hour;
    const endAbs = endPoint.dayIndex * 24 + endPoint.hour + 0.5;

    const rangeStart = Math.min(startAbs, endAbs);
    const rangeEnd = Math.max(startAbs + 0.5, endAbs);

    const startDayIndex = clamp(Math.floor(rangeStart / 24), 0, DAYS.length - 1);
    const endDayIndex = clamp(Math.floor((rangeEnd - 0.001) / 24), 0, DAYS.length - 1);

    const slots = [];

    for (let dayIndex = startDayIndex; dayIndex <= endDayIndex; dayIndex += 1) {
      const dayStartAbs = dayIndex * 24;
      const dayEndAbs = (dayIndex + 1) * 24;
      const segmentStart = Math.max(rangeStart, dayStartAbs) - dayStartAbs;
      const segmentEnd = Math.min(rangeEnd, dayEndAbs) - dayStartAbs;
      const selectedDate = weekDays[dayIndex];

      if (!selectedDate || segmentEnd <= segmentStart || !isTodayOrFuture(selectedDate)) {
        continue;
      }

      const normalizedSegmentEnd = segmentEnd >= 24 ? END_OF_DAY : segmentEnd;

      slots.push({
        date: toDateKey(selectedDate),
        dayIndex,
        start: segmentStart,
        end: normalizedSegmentEnd,
      });
    }

    if (!slots.length) return null;

    return {
      ...slots[0],
      slots,
    };
  };

  const startSelection = (event, dayIndex, hour) => {
    event.preventDefault();
    event.stopPropagation();

    const selectedDate = weekDays[dayIndex];

    if (!isTodayOrFuture(selectedDate)) {
      setCalendarMessage(
        "Past dates are locked. Please select today or a future date to create a Teams call or availability slot."
      );

      setTimeout(() => setCalendarMessage(""), 2500);
      return;
    }

    const startPoint = { dayIndex, hour };
    const slot = buildSelectionFromRange(startPoint, startPoint);

    setCalendarMessage("");
    setDragStart(startPoint);
    setSelectedSlot(slot);
    setIsDragging(true);
  };

  const startResize = (event, calendarEvent, edge) => {
    event.preventDefault();
    event.stopPropagation();

    const dayIndex = weekDays.findIndex((day) => toDateKey(day) === calendarEvent.date);
    if (dayIndex < 0) return;

    setResizingEvent({
      event: calendarEvent,
      edge,
      dayIndex,
      originalStart: calendarEvent.start,
      originalEnd: calendarEvent.end,
    });

    setSelectedSlot({
      date: calendarEvent.date,
      dayIndex,
      start: calendarEvent.start,
      end: calendarEvent.end,
      slots: [
        {
          date: calendarEvent.date,
          dayIndex,
          start: calendarEvent.start,
          end: calendarEvent.end,
        },
      ],
    });
  };

  const updateFromPointer = (event) => {
    maybeAutoScroll(event);

    const pointerSlot = getPointerSlot(event);
    if (!pointerSlot) return;

    if (isDragging && dragStart) {
      const nextSelection = buildSelectionFromRange(dragStart, pointerSlot);
      if (nextSelection) setSelectedSlot(nextSelection);
      return;
    }

    if (resizingEvent) {
      const nextHour = pointerSlot.hour;
      const minDuration = 0.5;

      let nextStart = resizingEvent.originalStart;
      let nextEnd = resizingEvent.originalEnd;

      if (resizingEvent.edge === "start") {
        nextStart = clamp(nextHour, 0, resizingEvent.originalEnd - minDuration);
      } else {
        nextEnd = clamp(nextHour + 0.5, resizingEvent.originalStart + minDuration, END_OF_DAY);
      }

      setSelectedSlot({
        date: resizingEvent.event.date,
        dayIndex: resizingEvent.dayIndex,
        start: nextStart,
        end: nextEnd,
        slots: [
          {
            date: resizingEvent.event.date,
            dayIndex: resizingEvent.dayIndex,
            start: nextStart,
            end: nextEnd,
          },
        ],
      });
    }
  };

  const finishInteraction = () => {
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);

      if (selectedSlot?.slots?.length) {
        onFinishSelection(
          selectedSlot.slots.length > 1 ? selectedSlot.slots : selectedSlot.slots[0]
        );
      }
      return;
    }

    if (resizingEvent) {
      const resizedSlot = selectedSlot?.slots?.[0] || selectedSlot;
      const originalEvent = resizingEvent.event;

      setResizingEvent(null);

      if (resizedSlot) {
        onFinishResize?.(originalEvent, resizedSlot);
      }
    }
  };

  useEffect(() => {
    if (!isDragging && !resizingEvent) return undefined;

    const handlePointerMove = (event) => updateFromPointer(event);
    const handlePointerUp = () => finishInteraction();

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, resizingEvent, dragStart, selectedSlot]);

  useEffect(() => {
    if (!scrollRef.current) return;

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    const currentTimeTop =
      currentHour * SLOT_HEIGHT + (currentMinute / 60) * SLOT_HEIGHT;

    const headerHeight = 46;

    scrollRef.current.scrollTop = Math.max(currentTimeTop - headerHeight, 0);
  }, []);

  const visibleSelectedSlots = selectedSlot?.slots || (selectedSlot ? [selectedSlot] : []);

  return (
    <div className="relative overflow-x-auto border-b border-slate-200">
      <AnimatePresence>
        {calendarMessage && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute w-full text-center z-40 rounded-b-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm font-medium text-amber-700"
          >
            {calendarMessage}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={scrollRef}
        className="max-h-[585px] overflow-y-auto"
      >
        <div
          ref={gridRef}
          className="grid min-w-[980px]"
          style={{
            gridTemplateColumns: `72px repeat(${DAYS.length}, minmax(118px, 1fr))`,
          }}
        >
          <div className="sticky top-0 z-[35] h-20 border-b border-slate-200 bg-white" />

          {weekDays.map((day, index) => {
            const dayKey = toDateKey(day);
            const isActiveDay = dayKey === activeDayKey;

            return (
              <div
                key={dayKey}
                className={[
                  "sticky top-0 z-[35] flex h-20 flex-col items-center justify-center border-b border-slate-200 text-center",
                  index >= 5 ? "bg-slate-50" : "bg-white",
                ].join(" ")}
              >
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  {DAYS[index]}
                </p>

                <p
                  className={[
                    "mt-1 inline-flex h-8 min-w-8 items-center justify-center rounded-xl text-sm font-semibold transition",
                    isActiveDay
                      ? "bg-[#365561] text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)]"
                      : "text-slate-900",
                  ].join(" ")}
                >
                  {day.getDate()}
                </p>
              </div>
            );
          })}

          <div
            className="relative border-r border-slate-200"
            style={{ height: HOURS.length * SLOT_HEIGHT }}
          >
            {HOURS.slice(1).map((hour) => {
              const isCurrentHour = hour === getCurrentHour();

              return (
                <div
                  key={hour}
                  ref={isCurrentHour ? currentHourRef : null}
                  className="absolute right-3 -translate-y-1/2 text-xs font-medium text-slate-400"
                  style={{ top: getTop(hour) }}
                >
                  {timeLabel(hour)}
                </div>
              );
            })}
          </div>

          {weekDays.map((day, dayIndex) => {
            const isWeekend = dayIndex >= 5;
            const isPastDay = !isTodayOrFuture(day);
            const selectedSegment = visibleSelectedSlots.find(
              (slot) => slot.dayIndex === dayIndex
            );

            return (
              <div
                key={toDateKey(day)}
                className={[
                  "relative border-r border-slate-200 last:border-r-0",
                  isWeekend ? "bg-slate-50/80" : "bg-white",
                  isPastDay ? "opacity-60" : "",
                ].join(" ")}
                style={{ height: HOURS.length * SLOT_HEIGHT }}
              >
                {HOURS.map((hour) => (
                  <React.Fragment key={hour}>
                    <div
                      className="absolute left-0 right-0 border-t border-slate-100"
                      style={{ top: getTop(hour) }}
                    />
                    {[0, 0.5].map((offset) => (
                      <button
                        key={offset}
                        type="button"
                        onPointerDown={(event) =>
                          startSelection(event, dayIndex, hour + offset)
                        }
                        className={[
                          "absolute left-1 right-1 rounded-xl transition focus:outline-none",
                          isPastDay
                            ? "cursor-not-allowed"
                            : "hover:bg-[#365561]/5",
                        ].join(" ")}
                        style={{
                          top: getTop(hour + offset),
                          height: SLOT_HEIGHT / 2,
                        }}
                      />
                    ))}
                  </React.Fragment>
                ))}

                {groupedEvents[dayIndex]?.map((event, index) => {
                  const isHovered = hoveredEventID === event.id;

                  return (
                    <div
                      key={event.id}
                      className="absolute overflow-visible"
                      style={{
                        top: getTop(event.start),
                        height: getHeight(event.start, event.end),
                        left: `${6 + index * 13}px`,
                        right: `${10 + Math.max(0, groupedEvents[dayIndex].length - index - 1) * 7}px`,
                        zIndex: isHovered ? 30 : 10 + index,
                      }}
                    >
                      <EventChip
                        event={event}
                        onDelete={onDelete}
                        onClick={onEventClick}
                        onResizeStart={event.type === "availability" && event.consultant === "Client" ? startResize : undefined}
                        onHoverChange={setHoveredEventID}
                        dayIndex={dayIndex}
                        getTooltipBounds={() => scrollRef.current?.getBoundingClientRect()}
                      />
                    </div>
                  );
                })}

                {selectedSegment && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="pointer-events-none absolute left-2 right-2 rounded-2xl border-2 border-dashed border-[#365561] bg-[#365561]/10"
                    style={{
                      top: getTop(selectedSegment.start),
                      height: getHeight(selectedSegment.start, selectedSegment.end),
                      zIndex: 20,
                    }}
                  >
                    <div className="flex h-full items-center justify-center text-xs font-semibold text-slate-700">
                      {timeLabel(selectedSegment.start)} - {timeLabel(selectedSegment.end)}
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthCalendar({ currentDate, events, onEventClick, onDelete, onDayClick, }) {
  const days = getMonthDays(currentDate);
  const currentMonth = currentDate.getMonth();

  return (
    <div>
      <div className="grid grid-cols-7 gap-2">
        {DAYS.map((day) => (
          <div key={day} className="h-8 flex items-center justify-center text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            {day}
          </div>
        ))}

        {days.map((day) => {
          const key = toDateKey(day);
          const dayEvents = events.filter((event) => event.date === key);
          const muted = day.getMonth() !== currentMonth;

          return (
            <div
              key={key}
              onClick={() => onDayClick(day)}
              className={[
                "min-h-[132px] rounded-2xl border p-2 transition",
                muted ? "border-slate-100 bg-slate-50/60" : "border-slate-200 bg-white",
              ].join(" ")}
            >
              <p className={["mb-2 text-xs font-semibold", muted ? "text-slate-300" : "text-slate-700"].join(" ")}>
                {day.getDate()}
              </p>

              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event) => (
                  <EventChip
                    key={event.id}
                    event={event}
                    compact
                    onClick={(clickedEvent) => {
                      onEventClick(clickedEvent);
                    }}
                    onDelete={onDelete}
                  />
                ))}

                {dayEvents.length > 3 && (
                  <p className="px-2 text-[11px] font-medium text-slate-400">
                    +{dayEvents.length - 3} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MobileCalendar({
  currentDate,
  events,
  onCreateClick,
  onCreateAvailability,
  onEventClick,
  onDelete,
  onClose,
  embeddedWizardMode = false,
}) {
  const [mobileMode, setMobileMode] = useState("agenda");
  const [selectedDate, setSelectedDate] = useState(baseDate);
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [availabilityForm, setAvailabilityForm] = useState({
    date: toDateKey(baseDate),
    start: 10,
    end: 11,
  });

  const agendaRefs = useRef({});
  const contentScrollRef = useRef(null);
  const dayContentRef = useRef(null);

  const visibleWeekStart = getMonday(selectedDate);
  const visibleWeek = Array.from({ length: 7 }, (_, i) => addDays(visibleWeekStart, i));
  const agendaDays = getAgendaRange(currentDate);

  const selectedDateKey = toDateKey(selectedDate);
  const compact = Boolean(embeddedWizardMode);

  const timeOptions = useMemo(
    () => Array.from({ length: 48 }, (_, index) => index * 0.5),
    []
  );

  const eventsBySelectedDay = events
    .filter((event) => event.date === selectedDateKey)
    .sort((a, b) => a.start - b.start);

  const agendaGroups = agendaDays
    .map((day) => {
      const key = toDateKey(day);
      return {
        date: day,
        dateKey: key,
        events: events.filter((event) => event.date === key).sort((a, b) => a.start - b.start),
      };
    })
    .filter((group) => group.events.length > 0);

  const scrollToNode = (node) => {
    if (!node || !contentScrollRef.current) return;

    const container = contentScrollRef.current;
    const containerRect = container.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const nodeTop = nodeRect.top - containerRect.top + container.scrollTop;

    container.scrollTo({
      top: Math.max(nodeTop - 12, 0),
      behavior: "smooth",
    });
  };

  const scrollToDay = (date) => {
    setSelectedDate(date);
    const key = toDateKey(date);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const targetNode =
          mobileMode === "agenda" ? agendaRefs.current[key] : dayContentRef.current;

        scrollToNode(targetNode);
      });
    });
  };

  const updateAvailabilityForm = (patch) => {
    setAvailabilityForm((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const openAvailabilityForm = () => {
    const dateKey = toDateKey(selectedDate);

    setAvailabilityForm({
      date: dateKey,
      start: 10,
      end: 11,
    });
    setShowAvailabilityForm(true);
  };

  const submitAvailabilityForm = (event) => {
    event.preventDefault();

    const formDate = new Date(`${availabilityForm.date}T12:00:00`);

    if (!isTodayOrFuture(formDate)) {
      Swal.fire("Past date", "Please choose today or a future date.", "warning");
      return;
    }

    if (Number(availabilityForm.end) <= Number(availabilityForm.start)) {
      Swal.fire("Invalid time", "End time must be after start time.", "warning");
      return;
    }

    const slot = {
      date: availabilityForm.date,
      start: Number(availabilityForm.start),
      end: Number(availabilityForm.end),
    };

    if (onCreateAvailability) {
      onCreateAvailability(slot);
    } else {
      onCreateClick?.(slot);
    }

    setShowAvailabilityForm(false);
    setMobileMode("agenda");
    scrollToDay(formDate);
  };

  return (
    <div className="md:hidden">
      <div className="relative flex h-[calc(95vh-60px)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.10)]">
        <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#365561]">
                Client Availability
              </p>
              <h2 className="mt-1 text-lg font-semibold leading-6 text-slate-900">
                Teams call and availability
              </h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800 focus:outline-none"
              aria-label="Close calendar"
            >
              <X className="h-4.5 w-4.5" />
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-slate-800">
              {selectedDate.toLocaleString("en-US", { month: compact ? "short" : "long" })} {selectedDate.getFullYear()}
            </p>

            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => scrollToDay(addDays(selectedDate, -7))}
                className="rounded-xl border border-slate-200 bg-white p-1 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
                aria-label="Previous week"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollToDay(addDays(selectedDate, 7))}
                className="rounded-xl border border-slate-200 bg-white p-1 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
                aria-label="Next week"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-2 inline-flex w-full rounded-2xl border border-slate-200 bg-slate-50 p-1">
            {["agenda", "day"].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setMobileMode(mode)}
                className={[
                  "flex-1 rounded-xl px-3 py-1.5 text-xs font-medium capitalize transition focus:outline-none",
                  mobileMode === mode
                    ? "bg-[#365561] text-white shadow-sm"
                    : "text-slate-500 hover:bg-white",
                ].join(" ")}
              >
                {mode}
              </button>
            ))}
          </div>

          <div className="mt-2 rounded-2xl border border-slate-100 bg-slate-50/80 p-2">
            <div className="grid grid-cols-7 text-center text-[10px] font-semibold text-slate-400">
              {MOBILE_WEEKDAYS.map((day, index) => (
                <span key={`${day}-${index}`}>{day}</span>
              ))}
            </div>

            <div className="mt-1.5 grid grid-cols-7 gap-1">
              {visibleWeek.map((day) => {
                const active = toDateKey(day) === selectedDateKey;
                const disabled = !isTodayOrFuture(day);

                return (
                  <button
                    key={toDateKey(day)}
                    type="button"
                    onClick={() => scrollToDay(day)}
                    className={[
                      "rounded-xl py-1.5 text-xs font-semibold transition focus:outline-none",
                      active
                        ? "bg-[#365561] text-white shadow-[0_8px_18px_rgba(54,85,97,0.20)]"
                        : disabled
                          ? "text-slate-300"
                          : "text-slate-600 hover:bg-white",
                    ].join(" ")}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div
          ref={contentScrollRef}
          className="min-h-0 flex-1 overflow-y-auto bg-slate-50/70 px-3 py-3"
        >
          <div className="space-y-3 pb-24">
            {mobileMode === "agenda" ? (
              agendaGroups.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center shadow-sm">
                  <p className="text-sm font-medium text-slate-600">No slots found</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Use Add to share your preferred availability.
                  </p>
                </div>
              ) : (
                agendaGroups.map((group) => (
                  <div
                    key={group.dateKey}
                    ref={(node) => {
                      agendaRefs.current[group.dateKey] = node;
                    }}
                    className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm"
                  >
                    <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {formatLongDate(group.dateKey)}
                    </h3>

                    <div className="mt-2 space-y-2">
                      {group.events.map((event) => (
                        <EventChip
                          key={event.id}
                          event={event}
                          onClick={onEventClick}
                          onDelete={onDelete}
                          embeddedWizardMode={compact}
                          disableTooltip
                        />
                      ))}
                    </div>
                  </div>
                ))
              )
            ) : (
              <div
                ref={dayContentRef}
                className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm"
              >
                <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                  {formatLongDate(selectedDateKey)}
                </h3>

                <div className="mt-2 space-y-2">
                  {eventsBySelectedDay.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-7 text-center">
                      <p className="text-sm font-medium text-slate-600">
                        No events for this day
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Tap Add to share availability for this date.
                      </p>
                    </div>
                  ) : (
                    eventsBySelectedDay.map((event) => (
                      <EventChip
                        key={event.id}
                        event={event}
                        onClick={onEventClick}
                        onDelete={onDelete}
                        embeddedWizardMode={compact}
                        disableTooltip
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={openAvailabilityForm}
          className="absolute bottom-5 right-5 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#365561] text-white shadow-[0_18px_40px_rgba(54,85,97,0.34)] transition hover:bg-[#2c444d] focus:outline-none"
          aria-label="Add client availability"
        >
          <Plus className="h-6 w-6" />
        </button>

        <AnimatePresence>
          {showAvailabilityForm && (
            <>
              <motion.button
                type="button"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
                onClick={() => setShowAvailabilityForm(false)}
                className="absolute inset-0 z-40 bg-slate-900/20 backdrop-blur-[1px] focus:outline-none"
                aria-label="Close availability form"
              />

              <motion.form
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.98 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                onSubmit={submitAvailabilityForm}
                className="absolute bottom-24 left-3 right-3 z-50 rounded-3xl border border-blue-100 bg-blue-50 p-3 shadow-[0_20px_55px_rgba(15,23,42,0.22)]"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      Add availability
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Share a preferred client timeslot.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAvailabilityForm(false)}
                    className="rounded-xl bg-white p-1.5 text-slate-500 shadow-sm focus:outline-none"
                    aria-label="Close availability form"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="col-span-2 block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                      Date
                    </span>
                    <input
                      type="date"
                      value={availabilityForm.date}
                      onChange={(event) => updateAvailabilityForm({ date: event.target.value })}
                      className="mt-1 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#365561]/40"
                    />
                  </label>

                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                      Start
                    </span>
                    <select
                      value={availabilityForm.start}
                      onChange={(event) => updateAvailabilityForm({ start: Number(event.target.value) })}
                      className="mt-1 w-full rounded-xl border border-blue-100 bg-white px-2 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#365561]/40"
                    >
                      {timeOptions.slice(0, -1).map((time) => (
                        <option key={time} value={time}>
                          {timeLabel(time)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                      End
                    </span>
                    <select
                      value={availabilityForm.end}
                      onChange={(event) => updateAvailabilityForm({ end: Number(event.target.value) })}
                      className="mt-1 w-full rounded-xl border border-blue-100 bg-white px-2 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#365561]/40"
                    >
                      {timeOptions.slice(1).map((time) => (
                        <option key={time} value={time}>
                          {timeLabel(time)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAvailabilityForm(false)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-500 focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white shadow-sm focus:outline-none"
                  >
                    Add availability
                  </button>
                </div>
              </motion.form>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
function MeetingDataCard({
  type,
  title,
  description,
  hasData = false,
  loading = false,
  onLoad,
  onView,
  onReload,
  onStream,
  onDownload,
  recordingUrl,
}) {
  const isVideo = type === "video";

  const config = {
    log: {
      loadText: "Load log",
      viewText: "View",
      reloadText: "Reload",
      LoadIcon: FileSearch,
      ViewIcon: Eye,
    },
    transcript: {
      loadText: "Load transcript",
      viewText: "View",
      reloadText: "Reload",
      LoadIcon: FileSearch,
      ViewIcon: Eye,
    },
    video: {
      loadText: "Generate link",
      viewText: "Stream",
      reloadText: "Download",
      LoadIcon: Link,
      ViewIcon: Play,
    },
  }[type];

  const LoadIcon = config.LoadIcon;
  const ViewIcon = config.ViewIcon;

  return (
    <div className="rounded-2xl flex flex-col justify-between border border-slate-200 bg-slate-50 p-3">
      <div>
      <p className="text-sm font-semibold text-slate-900">{title}</p>

      <p className="mt-1 min-h-[36px] text-xs leading-5 text-slate-500">
        {description}
      </p>
      </div>

      {!hasData ? (
        <button
          type="button"
          onClick={onLoad}
          disabled={loading}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-wait disabled:opacity-60 focus:outline-none"
        >
          {loading ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <LoadIcon className="h-3.5 w-3.5" />
          )}
          {loading ? "Loading..." : config.loadText}
        </button>
      ) : isVideo ? (
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={onStream}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 focus:outline-none"
          >
            <Play className="h-3.5 w-3.5" />
            Stream
          </button>

          <button
            type="button"
            onClick={onDownload}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2c444d] focus:outline-none"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </button>
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={onView}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 focus:outline-none"
          >
            <ViewIcon className="h-3.5 w-3.5" />
            {config.viewText}
          </button>

          <button
            type="button"
            onClick={onReload}
            disabled={loading}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2c444d] disabled:cursor-wait disabled:opacity-60 focus:outline-none"
          >
            <RefreshCw className={["h-3.5 w-3.5", loading ? "animate-spin" : ""].join(" ")} />
            {config.reloadText}
          </button>
        </div>
      )}
    </div>
  );
}

function AttendeeGroup({ title, attendees }) {
  return (
    <div>
      <p className="text-sm font-semibold text-slate-900">{title}</p>

      <div className="mt-3 space-y-2">
        {attendees.length === 0 ? (
          <p className="text-xs text-slate-500">No attendees found.</p>
        ) : (
          attendees.map((attendee) => (
            <AttendeeRow
              key={attendee.email}
              name={attendee.name}
              email={attendee.email}
              status={attendee.responseStatus}
            />
          ))
        )}
      </div>
    </div>
  );
}

function AttendeeRow({ name, email, status }) {
  const normalized = status || "notResponded";

  const statusStyle =
    normalized === "accepted"
      ? "bg-emerald-100 text-emerald-700"
      : normalized === "declined"
        ? "bg-rose-100 text-rose-700"
        : "bg-slate-200 text-slate-600";

  const label =
    normalized === "accepted"
      ? "Accepted"
      : normalized === "declined"
        ? "Declined"
        : "No response";

  return (
    <div className="rounded-xl bg-slate-100 px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-slate-800">
            {name || "Unknown"}
          </p>
          <p className="truncate text-[11px] text-slate-500">{email}</p>
        </div>

        <span
          className={[
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
            statusStyle,
          ].join(" ")}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

function stripHtml(html = "") {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

function formatMeetingLogDate(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDuration(isoDuration = "") {
  const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
  if (!match) return "";

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Math.floor(Number(match[3] || 0));

  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function getInitials(name = "") {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";

  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function getAvatarColor(name = "") {
  const colors = [
    "bg-blue-100 text-blue-700",
    "bg-emerald-100 text-emerald-700",
    "bg-purple-100 text-purple-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-cyan-100 text-cyan-700",
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

function TeamsAvatar({ name }) {
  return (
    <div
      className={[
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        getAvatarColor(name),
      ].join(" ")}
    >
      {getInitials(name)}
    </div>
  );
}

function normalizeTeamsLog(graphLogs = []) {
  return graphLogs
    .slice()
    .sort(
      (a, b) =>
        new Date(a.createdDateTime).getTime() -
        new Date(b.createdDateTime).getTime()
    )
    .map((item) => {
      const detailType = item.eventDetail?.["@odata.type"] || "";
      const time = item.createdDateTime;

      if (item.messageType === "message") {
        return {
          id: item.id,
          type: "message",
          fromUser: item.from?.user?.displayName || "Unknown",
          content: stripHtml(item.body?.content),
          messageTime: time,
        };
      }

      if (detailType.includes("chatRenamedEventMessageDetail")) {
        return {
          id: item.id,
          type: "system",
          content: `Chat renamed to "${item.eventDetail?.chatDisplayName || "Meeting chat"}"`,
          messageTime: time,
        };
      }

      if (detailType.includes("membersAddedEventMessageDetail")) {
        const names = item.eventDetail?.members
          ?.map((m) => m.displayName)
          .filter(Boolean)
          .join(", ");

        return {
          id: item.id,
          type: "system",
          content: names ? `${names} added to the chat` : "A member was added to the chat",
          messageTime: time,
        };
      }

      if (detailType.includes("membersJoinedEventMessageDetail")) {
        const names = item.eventDetail?.members
          ?.map((m) => m.displayName)
          .filter(Boolean)
          .join(", ");

        return {
          id: item.id,
          type: "join",
          content: `${names || "A participant"} joined the meeting`,
          messageTime: time,
        };
      }

      if (detailType.includes("membersLeftEventMessageDetail")) {
        const names = item.eventDetail?.members
          ?.map((m) => m.displayName)
          .filter(Boolean)
          .join(", ");

        return {
          id: item.id,
          type: "leave",
          content: `${names || "A participant"} left the meeting`,
          messageTime: time,
        };
      }

      if (detailType.includes("callStartedEventMessageDetail")) {
        return {
          id: item.id,
          type: "call",
          content: "Meeting started",
          messageTime: time,
        };
      }

      if (detailType.includes("callEndedEventMessageDetail")) {
        return {
          id: item.id,
          type: "call",
          content: `Meeting ended${item.eventDetail?.callDuration ? ` · ${formatDuration(item.eventDetail.callDuration)}` : ""}`,
          messageTime: time,
        };
      }

      if (detailType.includes("callTranscriptEventMessageDetail")) {
        return {
          id: item.id,
          type: "transcript",
          content: "Transcript is available",
          messageTime: time,
        };
      }

      if (detailType.includes("callRecordingEventMessageDetail")) {
        const status = item.eventDetail?.callRecordingStatus;
        const url = item.eventDetail?.callRecordingUrl;
        const duration = formatDuration(item.eventDetail?.callRecordingDuration);

        if (status === "initial") {
          return {
            id: item.id,
            type: "recording",
            content: "Recording started",
            messageTime: time,
          };
        }

        if (status === "chunkFinished") {
          return {
            id: item.id,
            type: "recording",
            content: "Recording is being processed",
            messageTime: time,
          };
        }

        if (status === "success") {
          return {
            id: item.id,
            type: "recording",
            content: duration
              ? `Recording is available · ${duration}`
              : "Recording is available",
            recordingUrl: url,
            messageTime: time,
          };
        }
      }

      return {
        id: item.id,
        type: "system",
        content: "Meeting activity updated",
        messageTime: time,
      };
    });
}

function TeamsMeetingTimeline({ rawLogs = [] }) {
  const logs = useMemo(() => normalizeTeamsLog(rawLogs), [rawLogs]);

  if (!logs.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
        <p className="text-sm font-medium text-slate-600">
          No meeting activity found.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {logs.map((item) => {
        const time = formatMeetingLogDate(item.messageTime);

        if (item.type === "message") {
          return (
            <div key={item.id} className="flex items-start gap-3">
              <div className="relative w-6 h-full">
                <div className="absolute top-6">
                  <TeamsAvatar name={item.fromUser} />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <p className="text-sm font-semibold text-slate-800">
                    {item.fromUser}
                  </p>
                  <p className="text-[11px] font-medium text-slate-400">
                    {time}
                  </p>
                </div>

                <div className="mt-1 max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-1 text-sm leading-6 text-slate-700">
                  {item.content}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={item.id} className="flex justify-center py-1">
            <div className="max-w-[85%] text-center">
              <p
                className={[
                  "text-[11px] font-medium leading-5",
                  item.type === "recording"
                    ? "text-[#365561]"
                    : item.type === "transcript"
                      ? "text-emerald-700"
                      : "text-slate-400",
                ].join(" ")}
              >
                {item.content}
                {time && <span className="text-slate-400"> · {time}</span>}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function parseTranscriptTime(time) {
  const [hh, mm, ssRaw] = time.split(":");
  const ss = ssRaw.split(".")[0];

  return `${hh}:${mm}:${ss}`;
}

function cleanTranscriptText(text) {
  return text
    .replace(/<\/?v[^>]*>/g, "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseWebVttTranscript(content = "") {
  const blocks = content
    .replace("WEBVTT", "")
    .split(/\r?\n\r?\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  return blocks
    .map((block, index) => {
      const lines = block.split(/\r?\n/);
      const timeLine = lines.find((line) => line.includes("-->"));
      const textLine = lines.find((line) => line.includes("<v "));

      if (!timeLine || !textLine) return null;

      const [startRaw, endRaw] = timeLine.split("-->").map((v) => v.trim());
      const speakerMatch = textLine.match(/<v\s+([^>]+)>/);
      const speaker = speakerMatch?.[1] || "Unknown";

      return {
        id: `${startRaw}-${index}`,
        speaker,
        start: parseTranscriptTime(startRaw),
        end: parseTranscriptTime(endRaw),
        text: cleanTranscriptText(textLine),
      };
    })
    .filter((item) => item?.text);
}

function normalizeTranscriptList(transcripts = []) {
  return transcripts.flatMap((transcript, transcriptIndex) => {
    const parsed = parseWebVttTranscript(transcript.content);

    return parsed.map((item) => ({
      ...item,
      transcriptId: transcript.id,
      transcriptIndex,
      createdDateTime: transcript.createdDateTime,
    }));
  });
}

function TeamsTranscriptSection({ transcripts = [] }) {
  const items = useMemo(
    () => normalizeTranscriptList(transcripts),
    [transcripts]
  );

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
        <p className="text-sm font-medium text-slate-600">
          No transcript found.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const previous = items[index - 1];
        const sameSpeakerAsPrevious = previous?.speaker === item.speaker;

        return (
          <div
            key={item.id}
            className={[
              "flex items-start gap-3",
              sameSpeakerAsPrevious ? "pt-0" : "pt-3",
            ].join(" ")}
          >
            <div className="relative w-7 shrink-0">
              {!sameSpeakerAsPrevious && (
                <div className="absolute top-6">
                  <TeamsAvatar name={item.speaker} />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              {!sameSpeakerAsPrevious && (
                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <p className="text-sm font-semibold text-slate-800">
                    {item.speaker}
                  </p>
                  <p className="text-[11px] font-medium text-slate-400">
                    {item.start}
                  </p>
                </div>
              )}

              <div
                className={[
                  "max-w-[88%] rounded-2xl bg-slate-100 px-4 py-2 text-sm leading-6 text-slate-700",
                  sameSpeakerAsPrevious
                    ? "ml-0 rounded-tl-2xl"
                    : "rounded-tl-sm",
                ].join(" ")}
              >
                {item.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PrintTranscriptSection({ event, transcripts = [] }) {
  const items = useMemo(
    () => normalizeTranscriptList(transcripts),
    [transcripts]
  );

  return (
    <div className="transcript-print-root">
      <div className="mb-6 border-b border-slate-200 pb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#365561]">
          Meeting transcript
        </p>

        <h1 className="mt-2 text-2xl font-bold text-slate-900">
          {event?.title || "Teams Call"}
        </h1>

        <p className="mt-2 text-sm text-slate-600">
          {event?.date ? formatLongDate(event.date) : ""} ·{" "}
          {event ? `${timeLabel(event.start)} - ${timeLabel(event.end)}` : ""}
        </p>
      </div>

      <div className="space-y-3">
        {items.map((item, index) => {
          const previous = items[index - 1];
          const sameSpeakerAsPrevious = previous?.speaker === item.speaker;

          return (
            <div
              key={item.id}
              className={[
                "transcript-print-message flex items-start gap-3",
                sameSpeakerAsPrevious ? "pt-0" : "pt-3",
              ].join(" ")}
            >
              <div className="relative w-8 shrink-0">
                {!sameSpeakerAsPrevious && (
                  <div className="absolute top-6">
                    <TeamsAvatar name={item.speaker} />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                {!sameSpeakerAsPrevious && (
                  <div className="mb-1 flex flex-wrap items-baseline gap-2">
                    <p className="text-sm font-semibold text-slate-800">
                      {item.speaker}
                    </p>
                    <p className="text-[11px] font-medium text-slate-400">
                      {item.start}
                    </p>
                  </div>
                )}

                <div
                  className={[
                    "max-w-[88%] rounded-2xl bg-slate-100 px-4 py-2 text-sm leading-6 text-slate-700",
                    sameSpeakerAsPrevious
                      ? "ml-0 rounded-tl-2xl"
                      : "rounded-tl-sm",
                  ].join(" ")}
                >
                  {item.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



function EventDetailsModal({ event, onClose, onMeetingDataFetched, onDelete, onCreateFromAvailability, onEventSynced, onModifyEvent, onMeetingSettingChanged, }) {
  const { API_URL } = useContext(UrlContext);

  const isAvailability = event?.type === "availability";
  const [detailsView, setDetailsView] = useState("overview");

  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [loadingLog, setLoadingLog] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingVideo, setLoadingVideo] = useState(false);

  const [meetingSettings, setMeetingSettings] = useState({
    recordTranscript: event?.isAutoRecord ?? true,
    meetingChat: event?.isChatDisabled ?? true,
    emailReminder: event?.isEmailReminder ?? true,
  });

  const [callLog, setCallLog] = useState(event.meetingLog);
  const [callTranscript, setCallTranscript] = useState(event.transcript);
  const [attendees, setAttendees] = useState({
    clientEvent: null,
    expertEvent: null,
  });
	const [clientInviteBody, setClientInviteBody] = useState({});

  const [recordingUrl, setRecordingUrl] = useState(event?.recordingUrl || "");

  const startDate = new Date(`${event.date}T12:00:00`);
  const monthYear = startDate.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
  const day = startDate.getDate();
  const weekday = startDate.toLocaleString("en-US", { weekday: "short" });

  const meetingLink =
    event.meetingLink ||
    event.joinUrl ||
    event.onlineMeetingUrl ||
    event.teamsLink ||
    "";

  function getConsultantDisplayName(event) {
    return (
      event.consultantDisplayName ||
      event.consultant ||
      event.realExpertName ||
      "Unknown Expert"
    );
  }

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const [toggleLoading, setToggleLoading] = useState({
    recordTranscript: false,
    meetingChat: false,
    emailReminder: false,
  });

  const updateMeetingSetting = async (key, value) => {
    const previousValue = meetingSettings[key];

    setMeetingSettings((prev) => ({
      ...prev,
      [key]: value,
    }));

    setToggleLoading((prev) => ({
      ...prev,
      [key]: true,
    }));

    try {
      let res;

      if (key === "recordTranscript") {
        res = await fetch(`${API_URL}allow-record-automatically`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            meetingID: event.meetingID,
            isAutoRecord: value,
          }),
        });
      }

      if (key === "emailReminder") {
        res = await fetch(`${API_URL}enable-disable-emailReminder`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            meetingID: event.meetingID,
            isEmailReminder: value,
          }),
        });
      }

      if (key === "meetingChat") {
        res = await fetch(`${API_URL}disable-event-chat/${event.meetingID}/${value}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
      }

      const data = await res.json();

      if (data.status !== "success") {
        setMeetingSettings((prev) => ({
          ...prev,
          [key]: previousValue,
        }));

        Swal.fire("Error!", data.text || "Failed to update meeting setting.", "error");
      } else {
				const eventPatchMap = {
					recordTranscript: { isAutoRecord: value },
					meetingChat: { isChatDisabled: value },
					emailReminder: { isEmailReminder: value },
				};

				onMeetingSettingChanged?.(event.meetingID, eventPatchMap[key]);
			}
    } catch (error) {
      setMeetingSettings((prev) => ({
        ...prev,
        [key]: previousValue,
      }));

      Swal.fire("Error!", "Error while updating meeting setting.", "error");
    } finally {
      setToggleLoading((prev) => ({
        ...prev,
        [key]: false,
      }));
    }
  };

  const copyMeetingLink = async () => {
    if (!meetingLink) {
      Swal.fire("No link", "No meeting link found for this event.", "info");
      return;
    }

    await navigator.clipboard.writeText(meetingLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const fetchCallLog = async () => {
    try {
      setLoading(true);
      setLoadingLog(true);

      const res = await fetch(`${API_URL}fetch-teams-meeting-log`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          meetingID: event.meetingID,
          accessToken: getToken(),
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setCallLog(data.teamsLog || []);

        const eventID = event.ID ?? event.id ?? event.eventID;
        onMeetingDataFetched?.(eventID, {
          meetingLog: data.teamsLog || [],
        });

      } else {
        Swal.fire("Error", data.text || "Failed to fetch meeting log.", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Error while fetching meeting log.", "error");
    } finally {
      setLoading(false);
      setLoadingLog(false);
    }
  };

  const fetchTranscript = async () => {
    try {
      setLoading(true);
      setLoadingTranscript(true);

      const res = await fetch(`${API_URL}fetch-teams-meeting-transcript`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          meetingID: event.meetingID,
          accessToken: getToken(),
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setCallTranscript(data.transcripts || []);
        
        const eventID = event.ID ?? event.id ?? event.eventID;
        onMeetingDataFetched?.(eventID, {
          transcript: data.transcripts || [],
        });

      } else {
        Swal.fire("Error", data.message || "Failed to fetch meeting log.", "error");
      }
    } catch {
      Swal.fire("Error", "Failed to fetch meeting transcript", "error");
    } finally {
      setLoading(false);
      setLoadingTranscript(false);
    }
  };

  const fetchAttendees = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_URL}track-event-attendees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: getToken(),
          meetingID: event.meetingID,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setAttendees({
          clientEvent: data.clientEvent,
          expertEvent: data.expertEvent,
        });
				if (data.clientEvent) {
					setClientInviteBody(data.clientEvent.body);
				};
      } else {
        Swal.fire("Error", data.text || "Failed to fetch attendees.", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Error while fetching attendees.", "error");
    } finally {
      setLoading(false);
    }
  };

  const syncWithOutlook = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_URL}sync-event-with-outlook`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: getToken(),
          meetingID: event.meetingID,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        onEventSynced?.(data.event);

        setMeetingSettings({
          recordTranscript: data.event.isAutoRecord ?? true,
          meetingChat: data.event.isChatDisabled ?? true,
          emailReminder: data.event.isEmailReminder ?? true,
        });

        await fetchAttendees();

        Swal.fire("Synced", "Meeting data synced with Outlook.", "success");
      } else {
        Swal.fire("Error", data.text || "Failed to sync meeting with Outlook.", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Error while syncing with Outlook.", "error");
    } finally {
      setLoading(false);
    }
  };

  const generateRecordingLink = async () => {
    try {
      setLoading(true);
      setLoadingVideo(true);

      const res = await fetch(`${API_URL}get-meeting-video-recording`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          meetingID: event.meetingID,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setRecordingUrl(data.recordingUrl || "");
      } else {
        Swal.fire("Error", data.text || "Failed to generate recording link.", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Error while generating recording link.", "error");
    } finally {
      setLoading(false);
      setLoadingVideo(false);
    }
  };

  const videoStreamUrl = recordingUrl
    ? `${API_URL}stream-recorded-meeting-video?videoURL=${encodeURIComponent(recordingUrl)}`
    : "";

  useEffect(() => {
    if (!isAvailability && event?.meetingID) {
      fetchAttendees();
    }
  }, [event?.meetingID]);

  // useEffect (() => {
  //   fetchCallLog();
  //   fetchTranscript();
  // }, []); 

  useEffect(() => {
    document.body.classList.add("printing-transcript-ready");

    return () => {
      document.body.classList.remove("printing-transcript-ready");
    };
  }, []);

  function LoadingCursorOverlay({ active }) {
    if (!active) return null;

    return (
      <div className="pointer-events-auto fixed inset-0 z-[9999] cursor-wait no-print">
        <div className="fixed right-6 bottom-6 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          <svg
            className="h-4 w-4 animate-spin text-[#365561]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          Updating calendar...
        </div>
      </div>
    );
  }

  if (isAvailability) {
    return (
      <>
        <motion.div
          className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[#365561]">
                  Availability slot
                </p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">
                  {event.title}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {formatLongDate(event.date)}, {timeLabel(event.start)} -{" "}
                  {timeLabel(event.end)}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="mt-1 text-sm text-slate-500">{getConsultantDisplayName(event)}</p>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => onDelete(event.id)}
                className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-100 focus:outline-none"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>

              <button
                disabled
                type="button"
                onClick={() => onCreateFromAvailability(event)}
                className="inline-flex items-center gap-2 rounded-2xl bg-[#365561]/60 px-4 py-2 text-sm font-medium text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:cursor-not-allowed focus:outline-none"
              >
                <Video className="h-4 w-4" />
                Create Teams Call
              </button>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        .transcript-print-root {
          display: none;
        }

        @page {
          size: A4;
          margin: 14mm;
        }

        @media print {
          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            overflow: visible !important;
            height: auto !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          body.printing-transcript-ready > #root > * {
            display: none !important;
          }

          .transcript-print-root {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            color: #0f172a !important;
            overflow: visible !important;
            height: auto !important;
            box-shadow: none !important;
          }

          .transcript-print-root,
          .transcript-print-root * {
            visibility: visible !important;
          }

          .no-print {
            display: none !important;
          }

          .transcript-print-message {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            margin-bottom: 10px !important;
          }

          .transcript-print-root * {
            box-shadow: none !important;
            animation: none !important;
            transition: none !important;
            transform: none !important;
          }
        }
      `}</style>

      {createPortal(
        <PrintTranscriptSection event={event} transcripts={callTranscript || []} />,
        document.body
      )}

      <motion.div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm no-print"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      <LoadingCursorOverlay active={loading} />

      <div className="fixed top-[78px] inset-0 z-50 flex items-center justify-center p-4 no-print">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          className="flex h-[calc(100vh-100px)] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
        >
          {/* Fixed title */}
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-3">
            <div>
              <p className="text-lg font-semibold text-[#365561]">{event.title}</p>
              <p className="mt-1 text-xs text-slate-500">
                {formatLongDate(event.date)}, {timeLabel(event.start)} -{" "}
                {timeLabel(event.end)}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid min-h-0 flex-1 gap-8 overflow-hidden px-5 lg:grid-cols-[240px_minmax(0,1fr)]">
            {/* Left fixed section */}
            <div className="hidden lg:flex items-start no-print">
              <div className="w-full space-y-3">
                <div className="mt-4 text-left text-[11px] leading-5 text-slate-400 mb-4">
                  <p>Transcripts and recordings are available only for recorded meetings.</p>
                </div>

                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="bg-[#365561] px-4 py-4 text-center">
                    <p className="text-md font-bold uppercase tracking-[0.18em] text-white">
                      {monthYear}
                    </p>
                  </div>

                  <div className="px-4 py-4 text-center">
                    <p className="text-sm font-medium uppercase tracking-[0.14em] text-slate-400">
                      {weekday}
                    </p>
                    <p className="mt-1 text-7xl font-semibold leading-none text-slate-900">
                      {day}
                    </p>
                    <p className="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
                      {timeLabel(event.start)} - {timeLabel(event.end)}
                    </p>
                  </div>
                </div>

                <div className="px-1 text-left text-xs leading-5 text-slate-500">
                  <p>
                    Created by{" "}
                    <span className="font-semibold text-slate-700">
                      {event.createdBy || event.owner || "Unknown"}
                    </span>
                  </p>
                  {/* <p>
                    Calendar{" "}
                    <span className="font-semibold text-slate-700">
                      {event.calendarUsed || event.calendarChoice || "Network"}
                    </span>
                  </p> */}
                  <p>
                    Created for{" "}
                    <span className="font-semibold text-slate-700">
                      {event.clientName || "Unknown"}
                    </span>
                  </p>
                </div>

                {/* <div className="mt-2 flex flex-row items-center gap-2">
                  <p className="text-sm font-semibold text-slate-500">{copied ? "Copied meeting link" : "Copy meeting link"}</p>
                  {copied ? 
                    <CheckCircleIcon className="h-4 w-4 text-green-500"/> 
                    : <Link 
                        className="h-4 w-4 text-[#365561] cursor-pointer"
                        onClick={copyMeetingLink}
                      />
                  }
                </div> */}
              </div>
            </div>

            {/* Right scrollable/switching section */}
            <div className="min-h-0 overflow-hidden">
              <AnimatePresence mode="wait">
                {detailsView === "overview" ? (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -28 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="h-full overflow-y-auto pr-1"
                  >
                    <div className="space-y-4 mt-4 mb-4">
                      <div className="cursor-not-allowed">
                        <section className="pointer-events-none opacity-50 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="text-sm font-semibold text-slate-900">
                            Meeting settings
                          </p>

                          <div className="mt-3 grid gap-3 md:grid-cols-3">
                            <ToggleSetting
                              label="Recording"
                              value={meetingSettings.recordTranscript}
                              loading={toggleLoading.recordTranscript}
                              onChange={(v) => updateMeetingSetting("recordTranscript", v)}
                            />

                            <ToggleSetting
                              label="Meeting chat"
                              value={meetingSettings.meetingChat}
                              loading={toggleLoading.meetingChat}
                              onChange={(v) => updateMeetingSetting("meetingChat", v)}
                            />

                            <ToggleSetting
                              label="Email reminder"
                              value={meetingSettings.emailReminder}
                              loading={toggleLoading.emailReminder}
                              onChange={(v) => updateMeetingSetting("emailReminder", v)}
                            />
                          </div>
                        </section>
                      </div>

                      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-sm font-semibold text-slate-900">
                          Meeting data
                        </p>

                        <div className="mt-3 grid gap-3 md:grid-cols-3">
                          <MeetingDataCard
                            type="log"
                            title="Meeting log"
                            description="View meeting activity, join events, leave events, and chat messages."
                            hasData={callLog?.length > 0}
                            loading={loadingLog}
                            onLoad={fetchCallLog}
                            onView={() => setDetailsView("log")}
                            onReload={fetchCallLog}
                          />

                          <MeetingDataCard
                            type="transcript"
                            title="Transcript"
                            description="Open or refresh the generated transcript for this recorded meeting."
                            hasData={Boolean(callTranscript)}
                            loading={loadingTranscript}
                            onLoad={fetchTranscript}
                            onView={() => setDetailsView("transcript")}
                            onReload={fetchTranscript}
                          />

                          <MeetingDataCard
                            type="video"
                            title="Meeting video"
                            description="Generate a secure recording link, then stream or download the video."
                            hasData={Boolean(recordingUrl)}
                            loading={loadingVideo}
                            onLoad={generateRecordingLink}
                            onStream={() => setDetailsView("video")}
                            onDownload={() => window.open(videoStreamUrl, "_blank")}
                          />
                        </div>
                      </section>

											<div className="grid gap-3 grid-cols-1">
                        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="text-sm font-semibold text-slate-900">
                            Meeting invite details
                          </p>

                          <div className="mt-3">
                            <div
															className="
																mt-3 rounded-xl border border-slate-500 bg-slate-50 p-3
																text-xs leading-relaxed text-slate-700
																[&_*]:!text-xs
																[&_*]:!leading-relaxed
																[&_h1]:!text-base
																[&_h2]:!text-sm
																[&_h3]:!text-sm
																[&_a]:!text-xs
																[&_a]:!text-blue-600
																[&_a]:!underline
																[&_a]:break-all
															"
															dangerouslySetInnerHTML={{
																__html: clientInviteBody?.content || "<p>No event body found.</p>",
															}}
														/>
                          </div>
                        </section>
                      </div>
											
											<div className="grid gap-3 md:grid-cols-2">
                        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="text-sm font-semibold text-slate-900">
                            Client attendees
                          </p>

                          <div className="mt-3">
                            <AttendeeGroup
                              attendees={attendees.clientEvent?.attendees || []}
                            />
                          </div>
                        </section>
                      </div>
                    </div>
                  </motion.div>
                ) : detailsView === 'log' ? (
                  <motion.div
                    key="log"
                    initial={{ opacity: 0, x: 36 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -36 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="h-full overflow-y-auto pr-1"
                  >
                    <div className="rounded-3xl border border-slate-200 bg-white mt-4 mb-4 shadow-sm">
                      <div className="sticky top-0 z-10 bg-white px-4 pt-4 pb-2 border-b border-slate-100 flex items-center justify-between gap-3 rounded-t-3xl shadow-sm">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            Meeting log
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            Timeline of meeting activity and chat messages.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setDetailsView("overview")}
                          className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
                        >
                          Back
                        </button>
                      </div>

                      <div className="p-4">
                        <TeamsMeetingTimeline rawLogs={callLog} />
                      </div>
                    </div>
                  </motion.div>
                ) : detailsView === 'transcript' ? (
                  <motion.div
                    key="log"
                    initial={{ opacity: 0, x: 36 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -36 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="h-full overflow-y-auto pr-1"
                  >
                    <div className="rounded-3xl border border-slate-200 bg-white mt-4 mb-4 shadow-sm">
                      <div className="sticky top-0 z-10 bg-white px-4 pt-4 pb-2 border-b border-slate-100 flex items-center justify-between gap-3 rounded-t-3xl shadow-sm">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            Meeting transcript
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            Full conversation transcript with speaker attribution and timestamps.
                          </p>
                        </div>

                        <div className="flex flex-row gap-2">
                          <button
                            type="button"
                            onClick={() => window.print()}
                            className="inline-flex items-center gap-2 rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white hover:bg-[#2c444d] focus:outline-none"
                          >
                            <Download className="h-3.5 w-3.5" />
                            PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => setDetailsView("overview")}
                            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
                          >
                            Back
                          </button>
                        </div>
                      </div>

                      <div className="p-4">
                        <TeamsTranscriptSection transcripts={callTranscript} />
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="log"
                    initial={{ opacity: 0, x: 36 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -36 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="h-full overflow-y-auto pr-1"
                  >
                    <div className="rounded-3xl border border-slate-200 bg-white mt-4 mb-4 shadow-sm">
                      <div className="sticky top-0 z-10 bg-white px-4 pt-4 pb-2 border-b border-slate-100 flex items-center justify-between gap-3 rounded-t-3xl shadow-sm">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            Meeting video
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            Securely stream or download the full meeting recording.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setDetailsView("overview")}
                          className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
                        >
                          Back
                        </button>
                      </div>

                      <div className="p-4">
                        {videoStreamUrl ? (
                          <div className="space-y-3">
                            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-sm">
                              <video
                                controls
                                preload="metadata"
                                className="aspect-video w-full bg-black"
                                src={videoStreamUrl}
                              >
                                Your browser does not support video playback.
                              </video>
                            </div>

                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => window.open(videoStreamUrl, "_blank")}
                                className="inline-flex items-center gap-2 rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white hover:bg-[#2c444d] focus:outline-none"
                              >
                                <Download className="h-3.5 w-3.5" />
                                Download
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                            <p className="text-sm font-medium text-slate-600">
                              No recording link generated yet.
                            </p>

                            <button
                              type="button"
                              onClick={generateRecordingLink}
                              disabled={loadingVideo}
                              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#365561] px-4 py-2 text-xs font-semibold text-white hover:bg-[#2c444d] disabled:cursor-wait disabled:opacity-60 focus:outline-none"
                            >
                              {loadingVideo ? (
                                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Link className="h-3.5 w-3.5" />
                              )}
                              {loadingVideo ? "Generating..." : "Generate video link"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}



function EmailChipInput({ label, emails, setEmails }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmail = () => {
    const email = value.trim();

    if (!email) return;

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (emails.includes(email)) {
      setError("This email is already added.");
      return;
    }

    setEmails((prev) => [...prev, email]);
    setValue("");
    setError("");
  };

  const removeEmail = (email) => {
    setEmails((prev) => prev.filter((item) => item !== email));
  };

  return (
    <div className="border-b border-slate-100 py-2">
      <div className="grid min-w-0 grid-cols-[48px_minmax(0,1fr)] items-start gap-3">
        <p className="pt-1 text-xs font-semibold text-slate-400">{label}</p>

        <div className="min-w-0">
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1.5 overflow-hidden">
            {emails.map((email) => (
              <span
                key={email}
                className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
              >
                <span className="min-w-0 truncate">{email}</span>

                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  className="shrink-0 text-slate-400 hover:text-rose-500"
                >
                  ×
                </button>
              </span>
            ))}

            <input
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError("");
              }}
              onBlur={addEmail}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addEmail();
                }
              }}
              placeholder="Add email"
              className="min-w-[70px] flex-1 bg-transparent py-1 text-xs text-slate-700 outline-none"
            />
          </div>

          {error && (
            <p className="mt-1 text-[11px] font-medium text-rose-500">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleSetting({ label, value, mode, loading = false, onChange }) {
  const disabled = loading || (label === "Anonymous Call" && mode === "modify");

  return (
    <button
      disabled={disabled}
      type="button"
      onClick={() => onChange(!value)}
      className={[
        "flex items-center justify-between rounded-2xl border px-4 py-3 transition focus:outline-none",
        value
          ? "border-[#365561]/20 bg-[#365561]/10"
          : "border-slate-200 bg-slate-50",
        disabled ? "cursor-not-allowed opacity-70" : "",
      ].join(" ")}
    >
      <span className="text-sm font-medium text-slate-700">{label}</span>

      <span
        className={[
          "flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold min-w-[40px]",
          value ? "bg-[#365561] text-white" : "bg-slate-200 text-slate-500",
        ].join(" ")}
      >
        {loading ? (
          <span className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : value ? (
          "On"
        ) : (
          "Off"
        )}
      </span>
    </button>
  );
}

function CreateEventModal({
  expertID,
  projectID,
  selectedSlot,
  defaultType = "teams",
  showTypeSelector = false,
  onClose,
  onCreate,
  mode = "create",
  existingEvent = null,
}) {
  const { API_URL } = useContext(UrlContext);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [editableSlot, setEditableSlot] = useState(selectedSlot);
  const steps = [
    "Call Settings",
    "Client Invitation",
    "Expert Invitation",
    "Overview",
  ];

  const [step, setStep] = useState(0);

  const [callSettings, setCallSettings] = useState({
    callType: "Expert Call",
    calendarChoice: "Network",
    start: selectedSlot.start,
    end: selectedSlot.end,
    recordTranscript: true,
    anonymousCall: false,
    meetingChat: true,
    emailReminder: true,
  });

  const [clientInvite, setClientInvite] = useState({
    to: [],
    cc: [],
    subject: "",
    duration: `${timeLabel(selectedSlot.start)} - ${timeLabel(selectedSlot.end)}`,
    body: "",
    currentUser: "user",
  });

  const [expertInvite, setExpertInvite] = useState({
    to: [],
    cc: [],
    subject: "",
    duration: `${timeLabel(selectedSlot.start)} - ${timeLabel(selectedSlot.end)}`,
    body: "",
    currentUser: "user",
  });

  function uniqueEmails(emails = []) {
    return [...new Set(emails.filter(Boolean))];
  }

  function buildInviteFromEvent(eventSide) {
    const attendees = eventSide?.attendees || [];

    return {
      to: uniqueEmails(
        attendees
          .filter((a) => a.type === "required")
          .map((a) => a.email)
      ),
      cc: uniqueEmails(
        attendees
          .filter((a) => a.type === "optional")
          .map((a) => a.email)
      ),
      subject: eventSide?.subject || "",
      body: eventSide?.body || "",
      currentUser: "user",
    };
  }

  function hourToTimeInput(hourValue) {
    return timeLabel(hourValue);
  }

  function timeInputToHour(value) {
    const [hour, minute] = value.split(":").map(Number);
    return hour + minute / 60;
  }

	const getCallDuration = (callType) => {
		return callType === "Get-to-Know" ? 0.5 : 1;
	};

  const fetchInvitationTemplate = async (value, callType) => {
    try {
      setEventsLoading(true);
      
      let url = callType === 'Expert Call' ? `get-expertCall-details` : `get-getToKnow-details`;
      const res = await fetch(`${API_URL}${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectID: projectID,
          expertID: expertID,
          isAnonymous: value,
          ephToken: getToken(),
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setClientInvite((prev) => ({
          ...prev,
          to: data.clientMail.clientAttendees,
          cc: data.clientMail.clientFrom,
          subject: data.clientMail.clientTitle,
          body: data.clientMail.clientMailBody,
          currentUser: data.clientMail.currentUser,
        }));

        setExpertInvite((prev) => ({
          ...prev,
          to: data.expertMail.expertAttendees,
          cc: data.expertMail.expertFrom,
          subject: data.expertMail.expertTitle,
          body: data.expertMail.expertMailBody,
          currentUser: data.expertMail.currentUser,
        }));
        return true;
      } else {
        return false;
      }
    } catch (error) {
      Swal.fire("Error", "Error while fetching Teams Call invitation template!", "error");
      return false;
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "modify") return;

    fetchInvitationTemplate(callSettings.anonymousCall, callSettings.callType);
  }, []);

  const updateCallSetting = (key, value) => {
    setCallSettings((prev) => ({ ...prev, [key]: value }));
  };

  const changeCallType = (value) => {
    updateCallSetting("callType", value);
		
		if (value==='Get-to-Know') {
			updateCallSetting("recordTranscript", false);
		} else {
			updateCallSetting("recordTranscript", true);
		}

		const nextEnd = editableSlot.start + getCallDuration(value);

		setCallSettings((prev) => ({
			...prev,
			callType: value,
			end: nextEnd,
		}));

		setEditableSlot((prev) => ({
			...prev,
			end: nextEnd,
		}));

    fetchInvitationTemplate(callSettings.anonymousCall, value);
  };

  const changeAnonymous = (value) => {
    updateCallSetting("anonymousCall", value);
		if (value) {
			updateCallSetting("meetingChat", false);
		} else {
			updateCallSetting("meetingChat", true);
		}
    fetchInvitationTemplate(value, callSettings.callType);
  };

  useEffect(() => {
    if (mode === "modify" && existingEvent) {
      setEditableSlot({
        date: existingEvent.date,
        start: existingEvent.start,
        end: existingEvent.end,
      });

      setCallSettings((prev) => ({
        ...prev,
        callType: existingEvent.title,
        calendarChoice: existingEvent.calendarUsed || "Network",
        start: existingEvent.start,
        end: existingEvent.end,
        recordTranscript: existingEvent.isAutoRecord ?? true,
        meetingChat: existingEvent.isChatDisabled ?? true,
        emailReminder: existingEvent.isEmailReminder ?? true,
      }));

      setClientInvite({
        ...buildInviteFromEvent(existingEvent.clientEvent),
        duration: `${timeLabel(existingEvent.start)} - ${timeLabel(existingEvent.end)}`,
      });

      setExpertInvite({
        ...buildInviteFromEvent(existingEvent.expertEvent),
        duration: `${timeLabel(existingEvent.start)} - ${timeLabel(existingEvent.end)}`,
      });
    }
  }, [mode, existingEvent]);

  const submit = () => {
    if (editableSlot.end <= editableSlot.start) {
      Swal.fire("Invalid time", "End time must be after start time.", "warning");
      return;
    }

    if (mode === "modify") {
      onCreate({
        ...existingEvent,
        callSettings,
        clientInvite,
        expertInvite,
        selectedSlot: editableSlot,
        isModify: true,
      });
      return;
    }

    onCreate({
      type: defaultType,
      title: callSettings.callType,
      project: "Project",
      consultant: "Consultant",
      callSettings,
      clientInvite,
      expertInvite,
      selectedSlot: editableSlot,
    });
  };

  function LoadingCursorOverlay({ active }) {
    if (!active) return null;

    return (
      <div className="pointer-events-auto fixed inset-0 z-[9999] cursor-wait">
        <div className="fixed right-6 bottom-6 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          <svg
            className="h-4 w-4 animate-spin text-[#365561]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          Updating calendar...
        </div>
      </div>
    );
  }

  return (
    <>
      <motion.div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      <div className="fixed top-[78px] inset-0 z-50 flex items-center justify-center p-4">
        <LoadingCursorOverlay active={eventsLoading} />
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          className="max-h-[calc(100vh-100px)] w-full max-w-5xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-[#365561]">
                {mode === "modify" ? "Modify Teams call" : "Create Teams call"}
              </p>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">
                Meeting initialization
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {formatLongDate(editableSlot.date)},{" "}
                {timeLabel(editableSlot.start)} - {timeLabel(editableSlot.end)}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-5">
            <div className="flex items-center">
              {[
                { label: "Call Settings", icon: Clock },
                { label: "Client Invitation", icon: Users },
                { label: "Expert Invitation", icon: Video },
                { label: "Overview", icon: CheckCircle2 },
              ].map((item, index) => {
                const Icon = item.icon;
                const active = index === step;
                const completed = index < step;

                return (
                  <React.Fragment key={item.label}>
                    <button
                      type="button"
                      onClick={() => setStep(index)}
                      className="flex min-w-0 flex-col items-center gap-1 focus:outline-none"
                    >
                      <span
                        className={[
                          "flex h-8 w-8 items-center justify-center rounded-xl transition",
                          active || completed
                            ? "bg-[#365561] text-white shadow-sm"
                            : "bg-white text-slate-400 border border-slate-200",
                        ].join(" ")}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span
                        className={[
                          "max-w-[90px] truncate text-[10px] font-semibold",
                          active ? "text-slate-900" : completed ? "text-[#365561]" : "text-slate-400",
                        ].join(" ")}
                      >
                        {item.label}
                      </span>
                    </button>

                    {index < 3 && (
                      <div
                        className={[
                          "mx-2 h-px flex-1 transition",
                          index < step ? "bg-[#365561]" : "bg-slate-200",
                        ].join(" ")}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {step === 0 && (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <label>
                  <span className="text-xs font-medium text-slate-500">
                    Call type
                  </span>
                  <select
                    disabled={mode === "modify"}
                    value={callSettings.callType}
                    onChange={(e) => changeCallType(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option>Expert Call</option>
                    <option>Get-to-Know</option>
                  </select>
                </label>

                <label>
                  <span className="text-xs font-medium text-slate-500">
                    Calendar choice
                  </span>
                  <select
                    disabled={mode === "modify"}
                    value={callSettings.calendarChoice}
                    onChange={(e) =>
                      updateCallSetting("calendarChoice", e.target.value)
                    }
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option>Network</option>
                    <option>Personal</option>
                  </select>
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <label>
                  <span className="text-xs font-medium text-slate-500">Date</span>
                  <input
                    type="date"
                    value={editableSlot.date}
                    onChange={(e) =>
                      setEditableSlot((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-[#365561]/30 focus:bg-white"
                  />
                </label>

                <label>
                  <span className="text-xs font-medium text-slate-500">Start time</span>
                  <input
                    type="time"
                    step="900"
                    value={hourToTimeInput(editableSlot.start)}
                    // onChange={(e) => {
                    //   const nextStart = timeInputToHour(e.target.value);

                    //   setEditableSlot((prev) => ({
                    //     ...prev,
                    //     start: nextStart,
                    //   }));

                    //   updateCallSetting("start", nextStart);
                    // }}
										onChange={(e) => {
											const nextStart = timeInputToHour(e.target.value);
											const nextEnd = nextStart + getCallDuration(callSettings.callType);

											setEditableSlot((prev) => ({
												...prev,
												start: nextStart,
												end: nextEnd,
											}));

											setCallSettings((prev) => ({
												...prev,
												start: nextStart,
												end: nextEnd,
											}));
										}}
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-[#365561]/30 focus:bg-white"
                  />
                </label>

                <label>
                  <span className="text-xs font-medium text-slate-500">End time</span>
                  <input
                    type="time"
                    step="900"
                    value={hourToTimeInput(editableSlot.end)}
                    onChange={(e) => {
                      const nextEnd = timeInputToHour(e.target.value);

                      setEditableSlot((prev) => ({
                        ...prev,
                        end: nextEnd,
                      }));

                      updateCallSetting("end", nextEnd);
                    }}
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-[#365561]/30 focus:bg-white"
                  />
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <ToggleSetting
                  label="Record / Transcript"
                  value={callSettings.recordTranscript}
                  mode={mode}
                  onChange={(v) => updateCallSetting("recordTranscript", v)}
                />
                <ToggleSetting
                  label="Anonymous Call"
                  value={callSettings.anonymousCall}
                  mode={mode}
                  onChange={(v) => {updateCallSetting("anonymousCall", v); changeAnonymous(v)}}
                />
                <ToggleSetting
                  label="Meeting Chat"
                  value={callSettings.meetingChat}
                  mode={mode}
                  onChange={(v) => updateCallSetting("meetingChat", v)}
                />
                <ToggleSetting
                  label="Email Reminder"
                  value={callSettings.emailReminder}
                  mode={mode}
                  onChange={(v) => updateCallSetting("emailReminder", v)}
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <InvitationStep
              title="Client Invitation"
              invite={clientInvite}
              setInvite={setClientInvite}
              loading={eventsLoading}
              mode={mode}
            />
          )}

          {step === 2 && (
            <InvitationStep
              title="Expert Invitation"
              invite={expertInvite}
              setInvite={setExpertInvite}
              loading={eventsLoading}
              mode={mode}
            />
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900">
                  {callSettings.callType}
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  {formatLongDate(editableSlot.date)},{" "}
                  {timeLabel(editableSlot.start)} - {timeLabel(editableSlot.end)}
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Calendar: {callSettings.calendarChoice}
                </p>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <ReviewEmail title="Client Email" invite={clientInvite} />
                <ReviewEmail title="Expert Email" invite={expertInvite} />
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-between gap-2 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
              disabled={step === 0}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 disabled:opacity-40 focus:outline-none"
            >
              Previous
            </button>

            {step < steps.length - 1 ? (
              <button
                type="button"
                onClick={() => setStep((prev) => prev + 1)}
                className="rounded-2xl bg-[#365561] px-4 py-2 text-sm font-medium text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] focus:outline-none"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                className="inline-flex items-center gap-2 rounded-2xl bg-[#365561] px-4 py-2 text-sm font-medium text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] focus:outline-none"
              >
                <Plus className="h-4 w-4" />
                {mode === "modify" ? "Update meeting" : "Create"}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
}

function InvitationStep({ title, invite, setInvite, loading, mode = "create" }) {
  const update = (key, value) => {
    setInvite((prev) => ({ ...prev, [key]: value }));
  };

  const Block = Quill.import("blots/block");
  Block.tagName = "div";
  Quill.register(Block, true);

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "arial",
    "georgia",
    "times-new-roman",
    "courier-new",
    "verdana",
    "tahoma",
    "lucida",
  ];
  Quill.register(Font, true);
  
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">
            Compose and adjust the outgoing invitation email.
          </p>
        </div>

        {loading && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500">
            Loading template...
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50 px-4 py-2">
          <p className="text-xs font-semibold text-slate-500">
            New message
          </p>
        </div>

        <div className="px-4">
          <EmailChipInput
            label="To"
            emails={invite.to}
            setEmails={(v) =>
              update("to", typeof v === "function" ? v(invite.to) : v)
            }
          />

          <EmailChipInput
            label="Cc"
            emails={invite.cc}
            setEmails={(v) =>
              update("cc", typeof v === "function" ? v(invite.cc) : v)
            }
          />

          <div className="grid grid-cols-[48px_1fr] items-center gap-3 border-b border-slate-100 py-2">
            <p className="text-xs font-semibold text-slate-400">Subject</p>
            <input
              disabled={mode === "modify"}
              value={invite.subject}
              onChange={(e) => update("subject", e.target.value)}
              placeholder="Email subject"
              className="w-full bg-transparent py-1 text-sm font-medium text-slate-800 outline-none"
            />
          </div>

          {/* <div
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => update("body", e.currentTarget.innerHTML)}
            dangerouslySetInnerHTML={{ __html: invite.body }}
            className="min-h-[230px] px-1 py-4 text-sm leading-6 text-slate-700 outline-none"
          /> */}

          <div className="min-h-[230px] text-sm leading-6 text-slate-700 outline-none">
            <ReactQuill
              readOnly={mode === "modify"}
              theme="snow"
              value={invite.body}
              onChange={(value) => {
                if (mode !== "modify") update("body", value);
              }}
              modules={{
                toolbar: [
                  [{ font: Font.whitelist }],
                  [{ header: [1, 2, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ list: "ordered" }, { list: "bullet" }],
                ],
              }}
              formats={[
                "font",
                "header",
                "bold",
                "italic",
                "underline",
                "strike",
                "list",
                "bullet",
              ]}
              style={{
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            />

            <style>
              {`
                .ql-editor {
                  font-family: Arial, sans-serif;
                  font-size: 14px;
                  line-height: 1.4;
                  padding: 5px !important;
                }
                .ql-container {
                  height: auto !important;
                  min-height: 100px;
                  padding-bottom: 10px;
                }
                .ql-container .ql-snow .ql-hidden {
                  display: none !important;
                }
                .ql-toolbar.ql-snow {
                  position: inherit;
                  top: 0;
                  right: 24px;
                  padding: 3px;
                  display: flex;
                  flex-wrap: nowrap;
                  gap: 10px;
                }
                .ql-font-arial { font-family: Arial, sans-serif; }
                .ql-font-georgia { font-family: Georgia, serif; }
                .ql-font-times-new-roman { font-family: "Times New Roman", serif; }
                .ql-font-courier-new { font-family: "Courier New", monospace; }
                .ql-font-verdana { font-family: Verdana, sans-serif; }
                .ql-font-tahoma { font-family: Tahoma, sans-serif; }
                .ql-font-lucida { font-family: "Lucida Sans Unicode", sans-serif; }
                .ql-font-roboto { font-family: "Roboto", sans-serif; }
                .ql-font-lato { font-family: "Lato", sans-serif; }

                /* Dropdown font preview fix */
                .ql-snow .ql-picker.ql-font .ql-picker-label::before,
                .ql-snow .ql-picker.ql-font .ql-picker-item::before {
                  content: attr(data-value);
                  font-family: inherit;
                }

                .ql-picker-item[data-value="arial"]::before,
                .ql-picker-label[data-value="arial"]::before {
                  font-family: Arial, sans-serif;
                  content: "Arial";
                }

                .ql-picker-item[data-value="georgia"]::before,
                .ql-picker-label[data-value="georgia"]::before {
                  font-family: Georgia, serif;
                  content: "Georgia";
                }

                .ql-picker-item[data-value="times-new-roman"]::before,
                .ql-picker-label[data-value="times-new-roman"]::before {
                  font-family: "Times New Roman", serif;
                  content: "Times New Roman";
                }

                .ql-picker-item[data-value="courier-new"]::before,
                .ql-picker-label[data-value="courier-new"]::before {
                  font-family: "Courier New", monospace;
                  content: "Courier New";
                }

                .ql-picker-item[data-value="verdana"]::before,
                .ql-picker-label[data-value="verdana"]::before {
                  font-family: Verdana, sans-serif;
                  content: "Verdana";
                }

                .ql-picker-item[data-value="tahoma"]::before,
                .ql-picker-label[data-value="tahoma"]::before {
                  font-family: Tahoma, sans-serif;
                  content: "Tahoma";
                }

                .ql-picker-item[data-value="lucida"]::before,
                .ql-picker-label[data-value="lucida"]::before {
                  font-family: "Lucida Sans Unicode", sans-serif;
                  content: "Lucida";
                }

                .ql-editor ul {
                  padding-left: 1.25em;
                  margin: 0.5em 0;
                }
              `}
            </style>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewEmail({ title, invite }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className="text-sm font-semibold text-slate-900">{title}</p>

      <div className="mt-3 space-y-1 text-xs">
        {/* TO */}
        <div className="grid grid-cols-[20px_minmax(0,1fr)] gap-2">
          <span className="font-semibold text-slate-400">To</span>
          <span className="min-w-0 break-words text-slate-600">
            {invite.to.length ? invite.to.join(", ") : "None"}
          </span>
        </div>

        {/* CC */}
        <div className="grid grid-cols-[20px_minmax(0,1fr)] gap-2">
          <span className="font-semibold text-slate-400">Cc</span>
          <span className="min-w-0 break-words text-slate-600">
            {invite.cc.length ? invite.cc.join(", ") : "None"}
          </span>
        </div>
      </div>

      {/* SUBJECT */}
      <div className="mt-1 border-t border-slate-100 pt-1">
        <p className="mt-1 text-sm font-semibold text-slate-800">
          {invite.subject || "No subject"}
        </p>
      </div>

      {/* BODY */}
      <div className="mt-2 border-t border-slate-100 pt-1">
        <div
          className="line-clamp-5 text-xs leading-5 text-slate-500"
          dangerouslySetInnerHTML={{ __html: invite.body }}
        />
      </div>
    </div>
  );
}

export default function EphCalendar({ callingFrom = "consultant", expertID, projectID, setOpenCall, setAppointmentExist, embeddedWizardMode = true }) {
  const { API_URL } = useContext(UrlContext);
  const [events, setEvents] = useState([]);
  const [calendarMode, setCalendarMode] = useState("week");
  const [currentDate, setCurrentDate] = useState(baseDate);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createDefaultType, setCreateDefaultType] = useState("availability");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [desktopCreateType, setDesktopCreateType] = useState("availability");
  const [isMobileCreateFlow, setIsMobileCreateFlow] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [currentConsultant, setCurrentConsultant] = useState('Current user');
  const [modalMode, setModalMode] = useState("create");
  const [modifyEvent, setModifyEvent] = useState(null);
	const [calendarEmailData, setCalendarEmailData] = useState({});

  const defaultTimezone = callingFrom === "consultant" ? DATABASE_TIMEZONE : localTimezone || DATABASE_TIMEZONE;
  const [selectedTimezone, setSelectedTimezone] = useState(defaultTimezone);

  const monthYear = currentDate.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

	useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  function LoadingCursorOverlay({ active }) {
    if (!active) return null;

    return (
      <div className="pointer-events-auto fixed inset-0 z-[9999] cursor-wait">
        <div className="fixed right-6 bottom-6 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          <svg
            className="h-4 w-4 animate-spin text-[#365561]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          Updating calendar...
        </div>
      </div>
    );
  }

	const handleMeetingSettingChanged = (meetingID, patch) => {
		setEvents((prev) =>
			prev.map((item) =>
				item.meetingID === meetingID
					? { ...item, ...patch }
					: item
			)
		);

		setSelectedEvent((prev) =>
			prev?.meetingID === meetingID
				? { ...prev, ...patch }
				: prev
		);
	};

  const deleteEvent = async (id) => {
    const beingDeleted = events.find((event) => event.id === id);
    if (beingDeleted.type === 'availability') {
      if (beingDeleted.consultant!=='Client') {
        Swal.fire('Denied', "You don't have access to delete an expert availability slots!", 'warning');
        return;
      }
      const success = await deleteAvailabilitySlots(beingDeleted);
      if (success) {
        setEvents((prev) => prev.filter((event) => event.id !== id));
        setSelectedEvent((prev) => (prev?.id === id ? null : prev));
      }
    } else {
      const success = await deleteTeamsEvent(beingDeleted);
      if (success) {
        setEvents((prev) => prev.filter((event) => event.id !== id));
        setSelectedEvent((prev) => (prev?.id === id ? null : prev));
      }
    }
  };

  const openCreateModal = (
    slot,
    defaultType = "teams",
    isMobile = false,
    mode = "create",
    event = null
  ) => {
    setSelectedSlot(slot);
    setCreateDefaultType(defaultType);
    setIsMobileCreateFlow(isMobile);
    setModalMode(mode);
    setModifyEvent(event);
    setCreateModalOpen(true);
  };

  const handleFinishSelection = (slot) => {
    const slots = Array.isArray(slot) ? slot : [slot];

    if (!slots.length) return;

    if (desktopCreateType === "availability") {
      slots.forEach((item, index) => {
        const dbSlot = convertSlotToDatabaseTimezone(
          item,
          selectedTimezone
        );

        const newEventID = Date.now() + index;

        setEvents((prev) => [
          ...prev,
          {
            id: newEventID,
            type: "availability",
            title: "Blocked availability",
            consultant: callingFrom === "consultant" ? currentConsultant : "Client",
            project: "Internal hold",
            date: dbSlot.date,
            start: dbSlot.start,
            end: dbSlot.end,
          },
        ]);

        saveAvailabilitySlots(
          {
            eventID: newEventID,
            expertID: expertID,
            projectID: projectID,
            startTime: slotToDateTime(dbSlot.date, dbSlot.start),
            endTime: slotToDateTime(dbSlot.date, dbSlot.end),
            timezone: "Europe/Berlin",
          },
        );
      });

      setSelectedSlot(null);
      return;
    }

    openCreateModal(slots[0], "teams");
  };

  const handleFinishResize = async (event, resizedSlot) => {
    if (!event || !resizedSlot) return;

    if (event.type !== "availability" || event.consultant !== "Client") {
      setSelectedSlot(null);
      return;
    }

    const dbSlot = convertSlotToDatabaseTimezone(resizedSlot, selectedTimezone);
    const nextEventID = Date.now();

    const success = await deleteAvailabilitySlots(event);
    if (!success) {
      setSelectedSlot(null);
      return;
    }

    setEvents((prev) =>
      prev.map((item) =>
        item.id === event.id
          ? {
              ...item,
              id: nextEventID,
              date: dbSlot.date,
              start: dbSlot.start,
              end: dbSlot.end,
            }
          : item
      )
    );

    await saveAvailabilitySlots({
      eventID: nextEventID,
      expertID: expertID,
      projectID: projectID,
      startTime: slotToDateTime(dbSlot.date, dbSlot.start),
      endTime: slotToDateTime(dbSlot.date, dbSlot.end),
      timezone: "Europe/Berlin",
    });

    setSelectedSlot(null);
  };

  const createClientAvailabilitySlot = async (slot) => {
    if (!slot) return;

    const dbSlot = convertSlotToDatabaseTimezone(
      slot,
      selectedTimezone
    );

    const newEventID = Date.now();

    setEvents((prev) => [
      ...prev,
      {
        id: newEventID,
        type: "availability",
        title: "Blocked availability",
        consultant: "Client",
        project: "Client preferred time",
        date: dbSlot.date,
        start: dbSlot.start,
        end: dbSlot.end,
      },
    ]);

    await saveAvailabilitySlots({
      eventID: newEventID,
      expertID: expertID,
      projectID: projectID,
      startTime: slotToDateTime(dbSlot.date, dbSlot.start),
      endTime: slotToDateTime(dbSlot.date, dbSlot.end),
      timezone: "Europe/Berlin",
    });

    setSelectedSlot(null);
  };

  const createEvent = async (formData) => {
    const activeSlot = formData.selectedSlot || selectedSlot;

    const dbSlot = convertSlotToDatabaseTimezone(
      activeSlot,
      selectedTimezone
    );

    const startTime = slotToDateTime(dbSlot.date, dbSlot.start);
    const endTime = slotToDateTime(dbSlot.date, dbSlot.end);

    if (formData.isModify) {
      const success = await updateTeamsEvent(formData, startTime, endTime);

      if (success) {
        setEvents((prev) =>
          prev.map((e) =>
            e.meetingID === formData.meetingID
              ? { ...e, 
									date: dbSlot.date, 
									start: dbSlot.start, 
									end: dbSlot.end, 
									isAutoRecord: formData.callSettings.recordTranscript, 
									isChatDisabled: formData.callSettings.meetingChat,
									isEmailReminder: formData.callSettings.emailReminder,
								}
              : e
          )
        );
      }

      setCreateModalOpen(false);
      return;
    }

    const newEventID = Date.now();

    const response = await createTeamsEvent(formData, startTime, endTime);

    if (response.success) {
      setEvents((prev) => [
        ...prev,
        {
          id: newEventID,
          meetingID: response.meetingID,
          type: formData.type,
          title:
            formData.title ||
            (formData.type === "teams" ? "Teams Call" : "Available"),
          consultant: formData.consultant || "Consultant",
          project: formData.project || "Project",
          date: dbSlot.date,
          start: dbSlot.start,
          end: dbSlot.end,
					isAutoRecord: formData.callSettings.recordTranscript,
          isChatDisabled: formData.callSettings.meetingChat,
          isEmailReminder: formData.callSettings.emailReminder,
        },
      ]);
    }

    setSelectedSlot(null);
    setCreateModalOpen(false);
    setIsMobileCreateFlow(false);
  };

  const handleEventClick = (event) => {
    if (event.type === "availability") {
      openCreateModal(
        {
          date: event.date,
          start: event.start,
          end: event.end,
        },
        "teams"
      );
      return;
    }

    setSelectedEvent(event);
  };

  const shiftPeriod = (amount) => {
    const next = new Date(currentDate);

    if (calendarMode === "month") {
      next.setMonth(next.getMonth() + amount);
    } else {
      next.setDate(next.getDate() + amount * 7);
    }

    setCurrentDate(next);
  };

  const openMobileCreate = () => {
    openCreateModal(
      {
        date: toDateKey(currentDate),
        start: 10,
        end: 11,
      },
      "teams",
      true
    );
  };

  const handleEventSynced = (updatedEvent) => {
    setEvents((prev) =>
      prev.map((item) =>
        item.meetingID === updatedEvent.meetingID || item.id === updatedEvent.id
          ? { ...item, ...updatedEvent }
          : item
      )
    );

    setSelectedEvent((prev) =>
      prev?.meetingID === updatedEvent.meetingID || prev?.id === updatedEvent.id
        ? { ...prev, ...updatedEvent }
        : prev
    );
  };

  const saveAvailabilitySlots = async (eventData) => {
    try {
      setEventsLoading(true);
      
      let url = callingFrom === 'consultant' ? `save-expert-meeting-time-slot` : `save-meeting-time-slot`;
      const res = await fetch(`${API_URL}${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ event: eventData }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        Swal.fire("Error", "Failed to add event!", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Error while adding events", "error");
    } finally {
      setEventsLoading(false);
    }
  };

  const deleteAvailabilitySlots = async (eventData) => {
    try {
      setEventsLoading(true);
      
      let url = eventData.consultant === 'Client' ? `delete-meeting-time-slot` : `delete-expert-meeting-time-slot`;
      const res = await fetch(`${API_URL}${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ eventID: eventData.id }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        Swal.fire("Error", "Failed to delete event!", "error");
        return false;
      }

      return true;
    } catch (error) {
      Swal.fire("Error", "Error while deleting events", "error");
      return false;
    } finally {
      setEventsLoading(false);
    }
  };

  const createTeamsEvent = async (eventData, startTime, endTime) => {
    try {
      setEventsLoading(true);
      
      let url = eventData.callSettings.calendarChoice === 'Network' ? `creat-outlook-meeting-from-default-calendar` : `creat-outlook-meeting`;
      const res = await fetch(`${API_URL}${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ephToken: getToken(),
          expertID: expertID,
          projectID: projectID,
          startTime: startTime,
          endTime: endTime,
          callType: eventData.callSettings.callType,
          mailForClient: eventData.clientInvite,
          mailForExpert: eventData.expertInvite,
          isAutoRecord: eventData.callSettings.recordTranscript,
          isChatDisabled: eventData.callSettings.meetingChat,
          isEmailReminder: eventData.callSettings.emailReminder,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        Swal.fire("Success!", data.text, "success");
        return { success: true, meetingID: data.meetingID};
      } else {
        Swal.fire("Error", "Failed to create event!", "error");
        return { success: false, meetingID: null};
      }
    } catch (error) {
      Swal.fire("Error", "Error while creating events", "error");
      return { success: false, meetingID: null};
    } finally {
      setEventsLoading(false);
    }
  };

  const updateTeamsEvent = async (eventData, startTime, endTime) => {
    try {
      setEventsLoading(true);

      const res = await fetch(`${API_URL}update-online-meeting`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accessToken: getToken(),
          meetingID: eventData.meetingID,
          meetingData: eventData,
          startTime,
          endTime,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        Swal.fire("Updated!", data.text, "success");
        return true;
      } else {
        Swal.fire("Error", data.text, "error");
        return false;
      }
    } catch {
      Swal.fire("Error", "Failed to update meeting", "error");
      return false;
    } finally {
      setEventsLoading(false);
    }
  };

  const deleteTeamsEvent = async (eventData) => {
    if (callingFrom==='client') {
      Swal.fire("Denied!", "You don't have access to delete a Teams Call!", "warning");
      return;
    }
    const result = await Swal.fire({
      title: "Delete Teams call?",
      text: "A cancelation mail will be sent to all existing attendees!",
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) {
      return false;
    }

    try {
      setEventsLoading(true);

      const res = await fetch(`${API_URL}delete-online-meeting`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: getToken(),
          meetingID: eventData.meetingID,
        }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        Swal.fire("Error", "Failed to delete event!", "error");
        return false;
      }

      return true;
    } catch (error) {
      Swal.fire("Error", "Error while deleting events", "error");
      return false;
    } finally {
      setEventsLoading(false);
    }
  };

    const displayEvents = useMemo(() => {
    return events.map((event) =>
      eventToDisplayEvent(event, selectedTimezone)
    );
  }, [events, selectedTimezone]);

  const fetchExpertCalendarEvents = async ( projectID) => {
    try {
      setEventsLoading(true);

      const res = await fetch(`${API_URL}get-all-events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectID: projectID }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setEvents(data.events || []);
        setCalendarEmailData(data.emailData || {});
      } else {
        Swal.fire("Error", "Failed to fetch events!", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Error while fetching events", "error");
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (!projectID) return;
    fetchExpertCalendarEvents(projectID);
  }, [projectID]);

  const updateEventMeetingData = React.useCallback(
    (eventID, updates) => {
      setEvents((previousEvents) =>
        previousEvents.map((event) => {
          const currentEventID =
            event.ID ?? event.id ?? event.eventID;

          if (String(currentEventID) !== String(eventID)) {
            return event;
          }

          return {
            ...event,
            ...updates,
          };
        })
      );

      // Keep the currently opened modal event synchronized too.
      setSelectedEvent((previousEvent) => {
        if (!previousEvent) return previousEvent;

        const currentEventID =
          previousEvent.ID ??
          previousEvent.id ??
          previousEvent.eventID;

        if (String(currentEventID) !== String(eventID)) {
          return previousEvent;
        }

        return {
          ...previousEvent,
          ...updates,
        };
      });
    },
    []
  );

  return (
    // <div className="h-full w-full overflow-y-auto bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_28%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] p-4 sm:p-6 lg:p-8">
		<div className="h-full w-full overflow-y-auto bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.11),transparent_28%),linear-gradient(to_bottom,rgba(248,250,252,0.9),rgba(238,242,246,0.9))] p-4 sm:p-6 lg:p-8">
      <LoadingCursorOverlay active={eventsLoading} />

      <MobileCalendar
        currentDate={currentDate}
        events={displayEvents}
        onCreateClick={openMobileCreate}
        onCreateAvailability={createClientAvailabilitySlot}
        onEventClick={setSelectedEvent}
        onDelete={deleteEvent}
        onClose={() => setOpenCall(false)}
        embeddedWizardMode={embeddedWizardMode}
      />

      <div className="mx-auto hidden max-w-[1600px] overflow-y-auto md:block">
        <div className="mb-6 flex flex-row gap-4 items-start justify-between">
          <div>
            <p className="text-sm font-medium text-[#365561]">
              Client Availability
            </p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
              Teams call and availability calendar
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Times are displayed in your local timezone for convenience. All meetings are created in Central European Time.
            </p>
          </div>

          <button 
						className="sticky top-0 z-10 rounded-2xl p-2.5 border border-slate-200 hover:bg-slate-100 hover:shadow hover:shadow-slate-200 focus:outline-none"
						onClick={() => setOpenCall(false)}
					> 
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="self-start rounded-3xl border border-slate-200/80 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
              <div className="flex w-full items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#365561]/10 text-[#365561]">
                    <CalendarDays className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex flex-row items-center gap-4">
                      <p className="text-xl font-semibold text-slate-900">
                        {calendarMode === "week" ? "Weekly planner" : "Monthly planner"}
                      </p>
                      <CalendarModeToggle value={calendarMode} onChange={setCalendarMode} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center justify-start gap-2">
                      <p className="font-semibold text-sm text-slate-600">{monthYear}</p>
                      <div className="flex flex-row gap-1">
                        <button
                          type="button"
                          onClick={() => shiftPeriod(-1)}
                          className="rounded-[10px] px-0.5 py-0.5 text-slate-600 transition border border-slate-300 hover:bg-gray-200 hover:shadow-sm focus:outline-none"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => shiftPeriod(1)}
                          className="rounded-[10px] px-0.5 py-0.5 text-slate-600 transition border border-slate-300 hover:bg-gray-200 hover:shadow-sm focus:outline-none"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row items-end gap-2">
                  <TimezoneSelector
                    selectedTimezone={selectedTimezone}
                    setSelectedTimezone={setSelectedTimezone}
                    localTimezone={localTimezone}
                    callingFrom={callingFrom}
                  />

                  <EventModeToggle
                    value={desktopCreateType}
                    onChange={setDesktopCreateType}
                  />
                </div>
              </div>
            </div>

            {calendarMode === "week" ? (
              <WeekCalendar
                currentDate={currentDate}
                events={displayEvents}
                selectedSlot={selectedSlot}
                setSelectedSlot={setSelectedSlot}
                onFinishSelection={handleFinishSelection}
                onFinishResize={handleFinishResize}
                // onEventClick={handleEventClick}
                onEventClick={setSelectedEvent}
                onDelete={deleteEvent}
              />
            ) : (
              <MonthCalendar
                currentDate={currentDate}
                events={displayEvents}
                // onEventClick={handleEventClick}
                onEventClick={setSelectedEvent}
                onDelete={deleteEvent}
                onDayClick={(day) => {
                  setCurrentDate(new Date(day));
                  setCalendarMode("week");
                }}
              />
            )}
          </div>

          <SidePanel
            projectID={projectID}
            events={displayEvents}
						emailData={calendarEmailData}
            selectedEvent={selectedEvent}
            onEventClick={setSelectedEvent}
            onDelete={deleteEvent}
          />
        </div>
      </div>

      <AnimatePresence>
        {createModalOpen && selectedSlot && (
          <CreateEventModal
            expertID={expertID}
            projectID={projectID}
            selectedSlot={selectedSlot}
            defaultType={createDefaultType}
            showTypeSelector={isMobileCreateFlow}
            mode={modalMode}
            existingEvent={modifyEvent}
            onClose={() => {
              setCreateModalOpen(false);
              setSelectedSlot(null);
              setIsMobileCreateFlow(false);
              setModalMode("create");
              setModifyEvent(null);
            }}
            onCreate={createEvent}
          />
        )}

        {selectedEvent && (
          <EventDetailsModal
            event={selectedEvent}
            onMeetingDataFetched={updateEventMeetingData}
            onClose={() => setSelectedEvent(null)}
            onDelete={deleteEvent}
            onEventSynced={handleEventSynced}
						onMeetingSettingChanged={handleMeetingSettingChanged}
            onModifyEvent={(event) => {
              setSelectedEvent(null);
              openCreateModal(
                {
                  date: event.date,
                  start: event.start,
                  end: event.end,
                },
                "teams",
                false,
                "modify",
                event
              );
            }}
            onCreateFromAvailability={(event) => {
              setSelectedEvent(null);
              openCreateModal(
                {
                  date: event.date,
                  start: event.start,
                  end: event.end,
                },
                "teams"
              );
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}